import { PROFILE_LABEL_SX } from "../profileFieldStyles";
// CandidateDetailsTabContent.jsx
import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Fade,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from "@mui/material";
import { Add, DocumentText1, Trash } from "iconsax-react";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import TabSectionHeader from "../TabSectionHeader";
import PopupMultiSelectField from "../PopupMultiSelectField";
import LocationMultiSelectField from "../LocationMultiSelectField";
import {
  groupLocationOption,
  normalizeLocationFilterValues,
} from "../../utils/locationFilters";

const BORDER = "#E5E7EB";
const MUTED = "#94A3B8";
const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const YELLOW_TOP_BORDER = "#FACC15";

const LABEL = "#6B7280";
const FIELD_BORDER = "#D9C2FF";
const FIELD_BG = "#FFFFFF";
const SECTION_LINE = "#93C5FD";
const PAGE_BG = "#FFFFFF";

const RESUME_UPLOAD_DIR = "candidate_resumes";
const RAW_API_BASE = (process.env.REACT_APP_API_BASE || "").trim();

console.log("RAW_API_BASE: " + RAW_API_BASE);

/** ------------------------- URL builders ------------------------- **/
function buildApiUrl(pathname) {
  const p = pathname?.startsWith("/") ? pathname : `/${pathname || ""}`;
  let base = RAW_API_BASE ? RAW_API_BASE.replace(/\/$/, "") : "/api";
  const hasApiSuffix = base === "/api" || base.endsWith("/api") || base.includes("/api/");
  if (!hasApiSuffix) base = `${base}/api`;
  return `${base}${p}`;
}

function safeTrim(v) {
  if (v === undefined || v === null) return "";
  return String(v).trim();
}

function toggleInCsv(csv, value) {
  const arr = normalizeCsvToArray(csv);
  const v = safeTrim(value);
  if (!v) return arr.join(", ");
  const next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
  return next.join(", ");
}

const SquareToggle = ({ checked, onClick }) => (
  <Box
    onClick={onClick}
    role="checkbox"
    aria-checked={checked}
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.();
      }
    }}
    sx={{
      width: 26,
      height: 26,
      borderRadius: "4px",
      border: `1px solid ${FIELD_BORDER}`,
      backgroundColor: "#FFFFFF",
      boxSizing: "border-box",
      cursor: "pointer",
      flex: "0 0 auto",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      outline: "none",
      transition: "none",
      "&:focus-visible": {
        outline: `1px solid ${FIELD_BORDER}`,
        outlineOffset: 0,
      },
      ...(checked
        ? {
            "&::after": {
              content: '""',
              width: 12,
              height: 12,
              borderRadius: "2px",
              backgroundColor: "#9CA3AF",
              display: "block",
            },
          }
        : {}),
    }}
  />
);

const PrefRow = ({ label, children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "180px 1fr" },
      columnGap: 3,
      rowGap: 1,
      alignItems: "start",
      py: 1.5,
    }}
  >
    <Typography className="profile-field-label" sx={{ ...PROFILE_LABEL_SX, pt: "10px" }}>{label}</Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

function buildWindowOrigin() {
  if (typeof window === "undefined" || !window.location) return "";
  return `${window.location.protocol}//${window.location.host}`.replace(/\/$/, "");
}

function buildPublicBase() {
  const raw = safeTrim(RAW_API_BASE);
  const windowOrigin = buildWindowOrigin();

  if (!raw) return windowOrigin;

  const base = raw.replace(/\/$/, "");

  if (base === "/api") return windowOrigin;

  if (/^https?:\/\//i.test(base) && base.endsWith("/api")) {
    return base.slice(0, -4);
  }

  if (/^https?:\/\//i.test(base)) {
    return base;
  }

  if (base.endsWith("/api")) {
    return `${windowOrigin}${base.slice(0, -4)}`.replace(/\/$/, "");
  }

  return `${windowOrigin}${base}`.replace(/\/$/, "");
}

function joinUrl(base, pathname) {
  const b = (base || "").replace(/\/$/, "");
  const p = (pathname || "").startsWith("/") ? pathname : `/${pathname || ""}`;
  return b ? `${b}${p}` : p;
}

function makeAbsolutePublicFileUrl(urlOrPath) {
  const raw = safeTrim(urlOrPath);
  if (!raw) return "";

  const publicBase = buildPublicBase();

  if (raw.startsWith("//")) {
    if (typeof window !== "undefined" && window.location?.protocol) {
      return `${window.location.protocol}${raw}`;
    }
    return `https:${raw}`;
  }

  try {
    const parsed = new URL(raw);
    const host = safeTrim(parsed.hostname).toLowerCase();

    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") {
      const rebuiltPath = `${parsed.pathname || ""}${parsed.search || ""}${parsed.hash || ""}`;
      return joinUrl(publicBase, rebuiltPath);
    }

    return parsed.toString();
  } catch {
    // continue
  }

  if (raw.startsWith("/")) {
    return joinUrl(publicBase, raw);
  }

  if (raw.startsWith("uploads/")) {
    return joinUrl(publicBase, `/${raw}`);
  }

  if (raw.includes("/uploads/")) {
    const idx = raw.indexOf("/uploads/");
    return joinUrl(publicBase, raw.slice(idx));
  }

  if (raw.startsWith("candidate_resumes/")) {
    return joinUrl(publicBase, `/uploads/${raw}`);
  }

  return joinUrl(publicBase, `/${raw.replace(/^\/+/, "")}`);
}

function buildCandidateResumeDocUrl(candidateId, filenameOrName) {
  if (!candidateId || !filenameOrName) return "";
  const safeName = String(filenameOrName).split("/").pop();
  return makeAbsolutePublicFileUrl(
    `/uploads/${RESUME_UPLOAD_DIR}/${candidateId}/documents/${encodeURIComponent(safeName)}`
  );
}

function getDocumentMergeKey(doc = {}) {
  const normalizedUrl = makeAbsolutePublicFileUrl(doc?.url || "");
  if (normalizedUrl) return `url:${normalizedUrl.toLowerCase()}`;

  const filename = safeTrim(doc?.filename || doc?.name || "");
  if (filename) return `file:${filename.toLowerCase()}`;

  return "";
}

function getDocumentQualityScore(doc = {}) {
  const filename = safeTrim(doc?.filename || "");
  const name = safeTrim(doc?.name || "");
  const sizeLabel = safeTrim(doc?.sizeLabel || "");
  const raw = doc?.raw && typeof doc.raw === "object" ? doc.raw : null;

  let score = 0;
  if (filename) score += 3;
  if (name && name.toLowerCase() !== "resume") score += 2;
  if (sizeLabel) score += 1;
  if (raw && Object.keys(raw).length) score += 1;
  return score;
}

async function safeJsonOrNull(res) {
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("application/json")) return res.json().catch(() => null);
  return null;
}

/** ------------------------- AUTH helper ------------------------- **/
function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("authToken") ||
    ""
  );
}

function authHeaders(extra = {}) {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}`, ...extra } : { ...extra };
}

/** ------------------------- robust parsing helpers ------------------------- **/
function safeString(v) {
  if (v === undefined || v === null) return "";
  return String(v);
}

function normalizeWebsiteHref(value) {
  const raw = safeTrim(value);
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function getOpeningImportTarget(job = {}) {
  const source = safeTrim(job?.source || job?.source_name || job?.provider || "").toLowerCase();
  const url = safeTrim(job?.job_url || job?.post_link || job?.url || "").toLowerCase();
  const isJobBoard = /linkedin|indeed|glassdoor|ziprecruiter|monster|careerbuilder|jobboard/.test(`${source} ${url}`);
  return isJobBoard ? "jobs" : "leads";
}

function tryJsonParseMaybe(v) {
  if (v === undefined || v === null) return null;
  if (typeof v === "object") return v;
  if (typeof v !== "string") return null;
  const s = v.trim();
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function normalizeSkillsFromCandidate(c) {
  const raw = c?.skills ?? c?.skills_text ?? "";
  if (Array.isArray(raw)) return raw.map((x) => safeTrim(x)).filter(Boolean);

  const s = safeTrim(raw);
  if (!s) return [];
  return s
    .split(",")
    .map((x) => safeTrim(x))
    .filter(Boolean);
}

function normalizeLocationsFromCandidate(c, prefObj) {
  const raw = prefObj?.locations ?? c?.pref_locations ?? null;

  if (raw === null || raw === undefined) return [];
  if (Array.isArray(raw)) return raw.map((x) => safeTrim(x)).filter(Boolean);

  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return [];
    const parsed = tryJsonParseMaybe(s);
    if (Array.isArray(parsed)) return parsed.map((x) => safeTrim(x)).filter(Boolean);
    return [s];
  }

  return [String(raw)];
}

function normalizeCsvToArray(v) {
  const s = safeTrim(v);
  if (!s) return [];
  return s
    .split(",")
    .map((x) => safeTrim(x))
    .filter(Boolean);
}

function normalizeArrayish(v) {
  if (Array.isArray(v)) return v.map((x) => safeTrim(x)).filter(Boolean);
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return [];
    const parsed = tryJsonParseMaybe(s);
    if (Array.isArray(parsed)) return parsed.map((x) => safeTrim(x)).filter(Boolean);
    return normalizeCsvToArray(s);
  }
  return [];
}

function normalizeSocialLinksArray(value, options = {}) {
  const { preserveEmpty = false, unique = false } = options;

  const finalize = (items) => {
    let arr = Array.isArray(items)
      ? items.map((x) => (x == null ? "" : String(x))).map((x) => x.trim())
      : [];

    if (!preserveEmpty) arr = arr.filter(Boolean);

    if (unique) {
      const seen = new Set();
      arr = arr.filter((item) => {
        const key = preserveEmpty ? item : item || "";
        if (!preserveEmpty && !key) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    return arr;
  };

  if (Array.isArray(value)) {
    return finalize(value);
  }

  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return preserveEmpty ? [""] : [];
    const parsed = tryJsonParseMaybe(s);

    if (Array.isArray(parsed)) {
      return finalize(parsed);
    }

    if (parsed && typeof parsed === "object") {
      return finalize(Object.values(parsed));
    }

    return finalize(s.split(","));
  }

  if (value && typeof value === "object") {
    return finalize(Object.values(value));
  }

  return preserveEmpty ? [""] : [];
}

function getFileExtension(nameOrUrl = "") {
  const clean = String(nameOrUrl).split("?")[0].split("#")[0];
  const parts = clean.split(".");
  return (parts[parts.length - 1] || "").toLowerCase();
}

function isPdfFile(doc) {
  const candidate = doc?.filename || doc?.name || doc?.url || "";
  return getFileExtension(candidate) === "pdf";
}

function isImageFile(doc) {
  const ext = getFileExtension(doc?.filename || doc?.name || doc?.url || "");
  return ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext);
}

function isResumeLikeDocument(doc = {}) {
  const type = safeTrim(
    doc?.kind || doc?.type || doc?.raw?.kind || doc?.raw?.type || ""
  ).toLowerCase();
  const name = safeTrim(doc?.name || doc?.filename || doc?.raw?.name || "").toLowerCase();
  const ext = getFileExtension(doc?.filename || doc?.name || doc?.url || "");

  return type === "resume" || name === "resume" || ["pdf", "doc", "docx"].includes(ext);
}

function normalizeDocumentArrayInput(value) {
  if (Array.isArray(value)) return value;
  const parsed = tryJsonParseMaybe(value);
  return Array.isArray(parsed) ? parsed : [];
}

function looksLikeStoredUploadReference(value = "") {
  const raw = safeTrim(value);
  if (!raw) return false;
  if (/^https?:\/\//i.test(raw)) return true;
  if (raw.startsWith("/uploads/")) return true;
  if (raw.startsWith("uploads/")) return true;
  if (raw.startsWith("candidate_resumes/")) return true;
  if (raw.startsWith("candidate_avatars/")) return true;
  if (raw.includes("/uploads/")) return true;
  return false;
}

function hasUsablePreviewUrl(doc = {}) {
  return Boolean(makeAbsolutePublicFileUrl(doc?.url || ""));
}

function getExplicitSocialLinksSource(candidateObj = {}) {
  if (!candidateObj || typeof candidateObj !== "object") {
    return {
      hasValue: false,
      value: undefined,
    };
  }

  const directKeys = ["social_links", "socialLinks", "social_links_text", "socialLinksText"];

  for (const key of directKeys) {
    if (Object.prototype.hasOwnProperty.call(candidateObj, key)) {
      return {
        hasValue: true,
        value: candidateObj[key],
      };
    }
  }

  const nestedSources = [
    candidateObj?.preferences_json,
    candidateObj?.preferencesJson,
    candidateObj?.preferences,
  ];

  for (const source of nestedSources) {
    const parsed = typeof source === "string" ? tryJsonParseMaybe(source) : source;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) continue;

    for (const key of directKeys) {
      if (Object.prototype.hasOwnProperty.call(parsed, key)) {
        return {
          hasValue: true,
          value: parsed[key],
        };
      }
    }
  }

  return {
    hasValue: false,
    value: undefined,
  };
}

function hasExplicitSocialLinks(candidateObj = {}) {
  return getExplicitSocialLinksSource(candidateObj).hasValue;
}

function getSocialLinksArray(candidateObj = {}, options = {}) {
  const { hasValue, value: direct } = getExplicitSocialLinksSource(candidateObj);

  if (hasValue) {
    return normalizeSocialLinksArray(direct, options);
  }

  return [
    candidateObj?.linkedin,
    candidateObj?.linkedin_url,
    candidateObj?.github,
    candidateObj?.github_url,
    candidateObj?.twitter,
    candidateObj?.twitter_url,
    candidateObj?.facebook,
    candidateObj?.facebook_url,
    candidateObj?.instagram,
    candidateObj?.instagram_url,
    candidateObj?.website,
    candidateObj?.website_url,
    candidateObj?.portfolio,
    candidateObj?.portfolio_url,
    candidateObj?.behance,
    candidateObj?.behance_url,
    candidateObj?.dribbble,
    candidateObj?.dribbble_url,
  ]
    .map((x) => safeTrim(x))
    .filter(Boolean)
    .filter((value, index, arr) => arr.indexOf(value) === index);
}

function getExtraFieldArray(candidateObj = {}, keys = []) {
  for (const key of keys) {
    const value = candidateObj?.[key];
    if (Array.isArray(value)) return value.map((x) => safeTrim(x));
    if (typeof value === "string" && value.trim()) {
      const parsed = tryJsonParseMaybe(value);
      if (Array.isArray(parsed)) return parsed.map((x) => safeTrim(x));
      return value
        .split(",")
        .map((x) => safeTrim(x))
        .filter(Boolean);
    }
  }
  return [];
}

function getExtraEmailsArray(candidateObj = {}) {
  return getExtraFieldArray(candidateObj, ["extra_emails", "extraEmails", "secondary_emails"]);
}

function getExtraPhonesArray(candidateObj = {}) {
  return getExtraFieldArray(candidateObj, ["extra_phones", "extraPhones", "secondary_phones"]);
}

function getExtraWebsitesArray(candidateObj = {}) {
  return getExtraFieldArray(candidateObj, ["extra_websites", "extraWebsites", "secondary_websites"]);
}

function buildLocationLabel(city, state) {
  return [safeTrim(city), safeTrim(state)].filter(Boolean).join(", ");
}

function splitFullName(fullNameValue = "") {
  const full = safeTrim(fullNameValue);
  if (!full) return { firstName: "", lastName: "" };

  const parts = full.split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

function splitLocationToCityState(locationValue = "") {
  const raw = safeTrim(locationValue);
  if (!raw) return { city: "", state: "" };

  const parts = raw
    .split(",")
    .map((x) => safeTrim(x))
    .filter(Boolean);

  if (!parts.length) return { city: "", state: "" };
  if (parts.length === 1) return { city: parts[0], state: "" };

  return {
    city: parts[0],
    state: parts.slice(1).join(", "),
  };
}

function normalizePhoneValue(v) {
  return safeString(v).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

function normalizeCandidateShape(rawCandidate = {}, options = {}) {
  const {
    preserveSocialLinkBlanks = false,
    preserveEditableWhitespace = false,
  } = options;

  const next = { ...(rawCandidate || {}) };

  const fullNameRaw = safeTrim(next.full_name || next.fullName || next.name || next.candidate_name || "");
  const splitName = splitFullName(fullNameRaw);

  const firstNameRaw =
    next.first_name ?? next.firstName ?? next.firstname ?? next.given_name ?? splitName.firstName ?? "";
  const lastNameRaw =
    next.last_name ??
    next.lastName ??
    next.lastname ??
    next.family_name ??
    next.surname ??
    splitName.lastName ??
    "";

  const firstName = preserveEditableWhitespace ? safeString(firstNameRaw) : safeTrim(firstNameRaw);
  const lastName = preserveEditableWhitespace ? safeString(lastNameRaw) : safeTrim(lastNameRaw);

  const fullName = fullNameRaw || [safeTrim(firstName), safeTrim(lastName)].filter(Boolean).join(" ").trim();

  const locationFromTable = safeTrim(
    next.location || next.location_label || next.current_location || next.resume_location || ""
  );

  const splitLocation = splitLocationToCityState(locationFromTable);

  const cityRaw = next.city ?? next.location_city ?? next.resume_city ?? next.parsed_city ?? splitLocation.city ?? "";
  const stateRaw =
    next.state ??
    next.location_state ??
    next.resume_state ??
    next.parsed_state ??
    next.region ??
    next.province ??
    splitLocation.state ??
    "";

  const city = preserveEditableWhitespace ? safeString(cityRaw) : safeTrim(cityRaw);
  const state = preserveEditableWhitespace ? safeString(stateRaw) : safeTrim(stateRaw);

  const phoneRaw =
    next.phone ??
    next.phone_number ??
    next.mobile ??
    next.mobile_phone ??
    next.cell ??
    next.telephone ??
    next.resume_phone ??
    "";

  const phone = preserveEditableWhitespace ? normalizePhoneValue(phoneRaw) : safeTrim(normalizePhoneValue(phoneRaw));

  next.first_name = firstName;
  next.last_name = lastName;
  next.full_name = fullName;
  next.name = fullName || next.name || "";
  next.city = city;
  next.state = state;
  next.location =
    safeTrim(next.location || "") ||
    buildLocationLabel(city, state) ||
    locationFromTable;

  next.phone = phone;
  next.phone_number = phone;

  const addressRaw =
    next.address ||
    next.street_address ||
    next.address_line_1 ||
    next.address1 ||
    next.resume_address ||
    "";

  next.address = preserveEditableWhitespace ? safeString(addressRaw) : safeTrim(addressRaw);

  const websiteRaw = next.website || next.website_url || "";
  next.website = preserveEditableWhitespace ? safeString(websiteRaw) : safeTrim(websiteRaw);
  next.website_url = preserveEditableWhitespace
    ? safeString(next.website_url || next.website || "")
    : safeTrim(next.website_url || next.website || "");

  const emailRaw = next.email || "";
  next.email = preserveEditableWhitespace ? safeString(emailRaw) : safeTrim(emailRaw);

  next.social_links = getSocialLinksArray(next, { preserveEmpty: preserveSocialLinkBlanks });

  return next;
}

function normalizeCandidateDraftShape(rawCandidate = {}) {
  return normalizeCandidateShape(rawCandidate, {
    preserveSocialLinkBlanks: true,
    preserveEditableWhitespace: true,
  });
}

function getCurrentExperienceSnapshot(experienceItems = []) {
  const items = safeArray(experienceItems);
  const currentItem =
    items.find((item) => /present|current|now|today/i.test(safeTrim(item?.to || ""))) ||
    items.find((item) => safeTrim(item?.jobTitle || item?.title || item?.position || "") || safeTrim(item?.company || item?.employer || "")) ||
    null;

  return {
    title: safeTrim(currentItem?.jobTitle || currentItem?.title || currentItem?.position || ""),
    company: safeTrim(currentItem?.company || currentItem?.employer || ""),
  };
}

function makeStableRowId(prefix, idx, fields = []) {
  const slug = fields
    .map((value) => safeTrim(value))
    .filter(Boolean)
    .join("-")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

  return `${prefix}-${idx}-${slug || "row"}`;
}

function buildUiModel({ candidate, parsedRaw }) {
  const expSource = parsedRaw?.experience ?? candidate?.experience_items ?? candidate?.experience ?? null;
  const eduSource = parsedRaw?.education ?? candidate?.education_items ?? candidate?.education ?? null;
  const prefSource = parsedRaw?.preferences ?? candidate?.preferences_json ?? candidate?.preferences ?? null;

  const expParsed = tryJsonParseMaybe(expSource);
  const eduParsed = tryJsonParseMaybe(eduSource);
  const prefParsed = tryJsonParseMaybe(prefSource);

  const experienceArr = safeArray(expParsed ?? expSource ?? []).map((e, idx) => ({
    id:
      e?.id ||
      makeStableRowId("exp", idx, [
        e?.jobTitle,
        e?.title,
        e?.position,
        e?.company,
        e?.employer,
        e?.from,
        e?.start,
        e?.startDate,
        e?.to,
        e?.end,
        e?.endDate,
      ]),
    jobTitle: safeTrim(e?.jobTitle || e?.title || e?.position || ""),
    company: safeTrim(e?.company || e?.employer || ""),
    companyWebsite: safeTrim(
      e?.companyWebsite ||
        e?.company_website ||
        e?.companyLink ||
        e?.company_link ||
        e?.company_profile_link ||
        e?.company_profile_url ||
        e?.company_url ||
        e?.website ||
        e?.url ||
        ""
    ),
    location: safeTrim(e?.location || ""),
    from: safeTrim(e?.from || e?.start || e?.startDate || ""),
    to: safeTrim(e?.to || e?.end || e?.endDate || ""),
    summary: safeTrim(e?.summary || e?.description || ""),
    bullets: safeArray(e?.bullets || e?.highlights || []).map((b) => safeTrim(b)).filter(Boolean),
  }));

  const educationArr = safeArray(eduParsed ?? eduSource ?? []).map((ed, idx) => ({
    id:
      ed?.id ||
      makeStableRowId("edu", idx, [
        ed?.school,
        ed?.institution,
        ed?.degree,
        ed?.field,
        ed?.major,
        ed?.from,
        ed?.start,
        ed?.startDate,
        ed?.to,
        ed?.end,
        ed?.endDate,
      ]),
    school: safeTrim(ed?.school || ed?.institution || ""),
    degree: safeTrim(ed?.degree || ""),
    field: safeTrim(ed?.field || ed?.major || ""),
    from: safeTrim(ed?.from || ed?.start || ed?.startDate || ""),
    to: safeTrim(ed?.to || ed?.end || ed?.endDate || ""),
    summary: safeTrim(ed?.summary || ""),
  }));

  const prefObj =
    prefParsed && typeof prefParsed === "object" && !Array.isArray(prefParsed) ? prefParsed : {};

  const preferences = {
    jobType: safeTrim(prefObj?.jobType || candidate?.pref_job_type || "Unknown") || "Unknown",
    workMode: safeTrim(prefObj?.workMode || candidate?.pref_work_mode || "Unknown") || "Unknown",
    salaryRange: safeTrim(prefObj?.salaryRange || candidate?.pref_salary_range || ""),
    locations: normalizeLocationsFromCandidate(candidate, prefObj),
    availability: safeTrim(prefObj?.availability || candidate?.pref_availability || ""),
    notes: safeTrim(prefObj?.notes || ""),
  };

  const advancedPreferences = {
    whyLooking: safeTrim(prefObj?.whyLooking || candidate?.pref_why_looking || ""),
    jobTypes: normalizeArrayish(prefObj?.jobTypes ?? candidate?.pref_job_types ?? ""),
    workModes: normalizeArrayish(prefObj?.workModes ?? candidate?.pref_work_modes ?? ""),
    timeline: safeTrim(prefObj?.timeline || candidate?.pref_timeline || ""),
    compensation: safeTrim(prefObj?.compensation || candidate?.pref_compensation || ""),
    needSponsorship: safeTrim(prefObj?.needSponsorship || candidate?.pref_need_sponsorship || ""),
    interviewAvailability: safeTrim(
      prefObj?.interviewAvailability || candidate?.pref_interview_availability || ""
    ),
  };

  const skills = Array.isArray(parsedRaw?.skills)
    ? parsedRaw.skills.map((x) => safeTrim(x)).filter(Boolean)
    : normalizeSkillsFromCandidate(candidate);

  return { experienceArr, educationArr, skills, preferences, advancedPreferences };
}

function makeId(prefix = "it") {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function resetOpsState(setter) {
  setter({ loading: false, error: "", success: "" });
}

function AutoHideSuccessAlert({ message, onClear, mt = 0, duration = 1000, fadeDuration = 1200 }) {
  const [open, setOpen] = useState(Boolean(message));

  useEffect(() => {
    if (!message) {
      setOpen(false);
      return undefined;
    }

    setOpen(true);

    const hideTimer = setTimeout(() => {
      setOpen(false);
    }, duration);

    const clearTimer = setTimeout(() => {
      onClear?.();
    }, duration + fadeDuration);

    return () => {
      clearTimeout(hideTimer);
      clearTimeout(clearTimer);
    };
  }, [message, onClear, duration, fadeDuration]);

  if (!message) return null;

  return (
    <Fade in={open} timeout={fadeDuration}>
      <Box sx={{ mt }}>
        <Alert severity="success">{message}</Alert>
      </Box>
    </Fade>
  );
}

function SectionTitle({ title, onEdit, rightText }) {
  return (
    <TabSectionHeader
      title={title}
      rightText={rightText || "Edit"}
      onAction={onEdit}
      dividerColor={SECTION_LINE}
      actionSx={{ color: PURPLE, fontWeight: 500 }}
    />
  );
}

function ExperienceSectionTitle({ onEdit }) {
  return (
    <TabSectionHeader
      title="Experience"
      dividerColor={SECTION_LINE}
      actionNode={
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Button
            variant="text"
            size="small"
            onClick={onEdit}
            sx={{ textTransform: "none", color: PURPLE, fontSize: 16, fontWeight: 500, p: 0, minWidth: 0 }}
          >
            Edit
          </Button>
        </Box>
      }
    />
  );
}

function EmptyBox({ height = 110 }) {
  return (
    <Box
      sx={{
        border: `1px dashed ${BORDER}`,
        borderRadius: 2,
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: MUTED,
        fontSize: 12,
        fontWeight: 400,
        background: "#fff",
      }}
    >
      Empty
    </Box>
  );
}

function PlainPlusIcon({ size = 18, color = PURPLE }) {
  return <Add size={size} color={color} variant="Linear" />;
}

function EditCard({ children, sx = {} }) {
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.75,
        border: `1px solid ${BORDER}`,
        borderRadius: 2,
        background: "#fff",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function FormRow({ label, children, right = null }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: right ? "140px 1fr 160px" : "140px 1fr" },
        gap: 1.5,
        alignItems: "center",
        py: 1.5,
      }}
    >
      <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>{label}</Typography>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
      {right ? <Box sx={{ justifySelf: "end" }}>{right}</Box> : null}
    </Box>
  );
}

function SubSectionHeader({ title, rightText }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
      <Typography sx={{ fontSize: 12, color: "#111827", fontWeight: 700 }}>{title}</Typography>
      {rightText ? (
        <Typography sx={{ fontSize: 11, color: "#94A3B8", fontWeight: 400 }}>{rightText}</Typography>
      ) : (
        <Box />
      )}

    </Box>
  );
}

function ViewCard({ children }) {
  return (
    <Box
      sx={{
        mt: 2,
        border: `1px solid ${BORDER}`,
        borderRadius: 2,
        background: "#fff",
        p: 1.5,
      }}
    >
      {children}
    </Box>
  );
}

function DocRow({ doc, onRemove, onOpen, removing, canRemove = true }) {
  const isRemoving = !!removing;
  return (
    <Box
      className="resume-container"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        border: `1px solid ${BORDER}`,
        borderRadius: 2,
        p: 1,
        background: "#fff",
        opacity: isRemoving ? 0.6 : 1,
        flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" },
        maxWidth: { xs: "100%", md: "calc(50% - 8px)" },
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 2,
          border: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F8FAFC",
          flex: "0 0 auto",
          cursor: doc?.url ? "pointer" : "default",
        }}
        onClick={() => (doc?.url ? onOpen?.(doc) : null)}
        title={doc?.url ? "Preview document" : ""}
      >
        <DocumentText1 size={18} color={BLUE} variant="Linear" />
      </Box>

      <Box
        sx={{ minWidth: 0, flex: 1, cursor: doc?.url ? "pointer" : "default" }}
        onClick={() => (doc?.url ? onOpen?.(doc) : null)}
        title={doc?.url ? "Preview document" : ""}
      >
        <Typography sx={{ fontSize: 13, color: "#111827", fontWeight: 500 }} noWrap>
          {doc.name}
        </Typography>
        <Typography sx={{ fontSize: 11, color: MUTED, fontWeight: 400 }}>
          {doc.kind || "Resume"}
          {doc.sizeLabel ? ` - ${doc.sizeLabel}` : ""}
        </Typography>
      </Box>

      {canRemove ? (
        <IconButton onClick={() => (isRemoving ? null : onRemove?.(doc))} size="small" disabled={isRemoving}>
          {isRemoving ? <CircularProgress size={16} /> : <Trash size={18} color="#EF4444" variant="Linear" />}
        </IconButton>
      ) : null}
    </Box>
  );
}

function SectionOpsNotice({ state, loadingText, onClearSuccess }) {
  if (!state?.loading && !state?.error && !state?.success) return null;

  return (
    <Box sx={{ mt: 1.25 }}>
      {state.loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <Typography sx={{ fontSize: 12, color: MUTED }}>{loadingText}</Typography>
        </Box>
      ) : null}

      {state.error ? (
        <Alert severity="error" sx={{ mt: state.loading ? 1 : 0 }}>
          {state.error}
        </Alert>
      ) : null}

      <AutoHideSuccessAlert
        message={state.success}
        onClear={onClearSuccess}
        mt={state.loading || state.error ? 1 : 0}
      />
    </Box>
  );
}

function ResumePreviewDialog({ doc, open, onClose, onAddNew }) {
  if (!open || !doc) return null;

  const previewUrl = makeAbsolutePublicFileUrl(doc?.url || "");
  const normalizedDoc = { ...doc, url: previewUrl };

  const isPdf = isPdfFile(normalizedDoc);
  const isImage = isImageFile(normalizedDoc);
  const extension = getFileExtension(normalizedDoc?.filename || normalizedDoc?.name || normalizedDoc?.url || "");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 24px)", md: "min(1100px, calc(100% - 80px))" },
          maxWidth: "1100px",
          height: { xs: "calc(100% - 24px)", md: "calc(100% - 64px)" },
          maxHeight: "960px",
          borderRadius: 3,
          overflow: "hidden",
          background: "#fff",
        },
      }}
    >
      <Box
        sx={{
          px: 2.25,
          py: 1.5,
          borderBottom: `1px solid ${SECTION_LINE}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography data-popup-section sx={{ fontWeight: 700, color: BLUE, fontSize: 15 }}>Resume</Typography>
          <Typography sx={{ fontSize: 12, color: MUTED }} noWrap>
            {normalizedDoc?.name || "Resume preview"}
          </Typography>
        </Box>

        <Box data-popup-header sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Button
            component="a"
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              color: PURPLE,
              fontWeight: 500,
            }}
          >
            Open in new tab
          </Button>

          <Button
            size="small"
            onClick={onAddNew}
            sx={{
              textTransform: "none",
              color: PURPLE,
              fontWeight: 500,
            }}
          >
            Add New Resume
          </Button>

          <IconButton data-popup-close onClick={onClose} size="small">
            <CloseRoundedIcon sx={{ fontSize: 18, color: "#475467" }} />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          height: "100%",
          overflow: "auto",
          background: "#fff",
          p: { xs: 1.5, md: 2 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            mx: "auto",
            background: "#fff",
            overflow: "hidden",
            borderTop: `7px solid ${YELLOW_TOP_BORDER}`,
            borderRadius: 1,
          }}
        >
          {isPdf ? (
            <iframe
              title={normalizedDoc?.name || "Resume preview"}
              src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
              style={{
                width: "100%",
                height: "calc(100vh - 180px)",
                minHeight: "620px",
                border: "0",
                outline: "none",
                display: "block",
                background: "#fff",
              }}
            />
          ) : isImage ? (
            <Box sx={{ display: "flex", justifyContent: "center", background: "#fff" }}>
              <Box
                component="img"
                src={previewUrl}
                alt={normalizedDoc?.name || "Resume preview"}
                sx={{
                  width: "100%",
                  maxWidth: 820,
                  height: "auto",
                  display: "block",
                  background: "#fff",
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                minHeight: 320,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                flexDirection: "column",
                textAlign: "center",
                gap: 1,
                background: "#fff",
              }}
            >
              <DocumentText1 size={34} color={BLUE} variant="Linear" />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                Preview is not supported for this file type
              </Typography>
              <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 400 }}>
                {extension ? `.${extension.toUpperCase()} file` : "Document"} can be opened in a new tab.
              </Typography>
              <Button
                component="a"
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={{
                  mt: 1,
                  textTransform: "none",
                  bgcolor: PURPLE,
                  borderRadius: 999,
                  "&:hover": { bgcolor: PURPLE },
                }}
              >
                Open document
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

function bytesToLabel(bytes) {
  if (!Number.isFinite(bytes)) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

async function mockParseResume(file) {
  await new Promise((r) => setTimeout(r, 900));
  const name = (file?.name || "").toLowerCase();
  return {
    full_name: name.includes("phoenix") ? "Phoenix Baker" : "Olivia Rhye",
    phone: "+351 951 726 606",
    city: "New York",
    state: "USA",
    location: "New York, USA",
    address: "123 Main St",
    experience: [
      {
        jobTitle: name.includes("engineer") ? "Software Engineer" : "Account Executive",
        company: "Example Company",
        location: "Los Angeles, CA",
        from: "2022",
        to: "Present",
        summary: "Led cross-functional projects and collaborated with stakeholders.",
        bullets: ["Built and maintained scalable features.", "Improved internal workflows."],
      },
    ],
    skills: ["Communication", "CRM", "Sales", "Negotiation", "Client Relationship Management"],
    education: [
      {
        school: "Long Beach State",
        degree: "Business Administration",
        field: "Marketing",
        from: "2016",
        to: "2020",
      },
    ],
    preferences: {
      jobType: "Full-time",
      workMode: "Hybrid",
      salaryRange: "$80,000 - $100,000",
      locations: ["Los Angeles, CA"],
      availability: "2 weeks",
      notes: "",
      whyLooking: "",
      jobTypes: ["Full-time"],
      workModes: ["Hybrid"],
      timeline: "",
      compensation: "",
      needSponsorship: "",
      interviewAvailability: "",
    },
  };
}

export default function CandidateDetailsTabContent({
  candidate,
  fieldOptions = {},
  externalEditTrigger = 0,
}) {
  const candidateId = candidate?.id;

  const [candidateLive, setCandidateLive] = useState(candidate ? normalizeCandidateDraftShape(candidate) : null);
  const [candidateLoad, setCandidateLoad] = useState({ loading: false, error: "" });

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [activeEditSection, setActiveEditSection] = useState("");
  const [parsedRaw, setParsedRaw] = useState(null);

  const [parseState, setParseState] = useState({ loading: false, error: "" });
  const [docState, setDocState] = useState({ loading: false, error: "" });
  const [saveParsedState, setSaveParsedState] = useState({ loading: false, error: "", success: "" });
  const [deleteState, setDeleteState] = useState({ loading: false, filename: "", error: "" });
  const [profileOps, setProfileOps] = useState({ loading: false, error: "", success: "" });
  const [avatarOps, setAvatarOps] = useState({ loading: false, error: "", success: "" });

  const [expOps, setExpOps] = useState({ loading: false, error: "", success: "" });
  const [eduOps, setEduOps] = useState({ loading: false, error: "", success: "" });
  const [skillsOps, setSkillsOps] = useState({ loading: false, error: "", success: "" });
  const [prefOps, setPrefOps] = useState({ loading: false, error: "", success: "" });
  const [docsOps, setDocsOps] = useState({ loading: false, error: "", success: "" });
  const [companyScanDialogOpen, setCompanyScanDialogOpen] = useState(false);
  const [openingJobsDialogOpen, setOpeningJobsDialogOpen] = useState(false);
  const [selectedExperienceCompanies, setSelectedExperienceCompanies] = useState([]);
  const [scannedOpeningJobs, setScannedOpeningJobs] = useState([]);
  const [selectedOpeningJobIds, setSelectedOpeningJobIds] = useState([]);
  const [companyScanState, setCompanyScanState] = useState({ loading: false, saving: false, completed: 0, total: 0, companyName: "", error: "" });
  const [existingExperienceCompanyNames, setExistingExperienceCompanyNames] = useState(new Set());
  const [companyExistenceLoading, setCompanyExistenceLoading] = useState(false);

  const [expDraft, setExpDraft] = useState([]);
  const [eduDraft, setEduDraft] = useState([]);
  const [skillsDraft, setSkillsDraft] = useState("");

  const [prefDraft, setPrefDraft] = useState({
    whyLooking: "",
    jobTypesCsv: "",
    workModesCsv: "",
    timeline: "",
    compensation: "",
    needSponsorship: "",
    interviewAvailability: "",
    jobType: "Unknown",
    workMode: "Unknown",
    salaryRange: "",
    locationsCsv: "",
    locationsList: [],
    availability: "",
    notes: "",
  });

  const [documentsDirty, setDocumentsDirty] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const fileInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    if (candidate && candidate?.id) setCandidateLive(normalizeCandidateDraftShape(candidate));
  }, [candidate]);

  useEffect(() => {
    if (!externalEditTrigger) return;

    resetOpsState(setProfileOps);
    setActiveEditSection("");
    setIsEditingProfile(true);
  }, [externalEditTrigger]);

  const candidateName = candidateLive?.full_name || candidateLive?.name || "Candidate";
 const candidateAvatarSrc = useMemo(() => {
  const raw =
    candidateLive?.avatar_url ||
    candidateLive?.avatar ||
    candidateLive?.photo_url ||
    candidateLive?.photo ||
    candidateLive?.profile_photo ||
    candidateLive?.image_url ||
    candidateLive?.image ||
    "";

  return raw ? makeAbsolutePublicFileUrl(raw) : "";
}, [candidateLive]);

  const socialLinksArray = useMemo(
    () => getSocialLinksArray(candidateLive || {}, { preserveEmpty: true }),
    [candidateLive]
  );
  const extraEmailsArray = useMemo(() => getExtraEmailsArray(candidateLive || {}), [candidateLive]);
  const extraPhonesArray = useMemo(() => getExtraPhonesArray(candidateLive || {}), [candidateLive]);
  const extraWebsitesArray = useMemo(() => getExtraWebsitesArray(candidateLive || {}), [candidateLive]);

  const candidateInitials = useMemo(() => {
    const full = String(candidateName || "").trim();
    if (!full) return "C";
    const parts = full.split(/\s+/).filter(Boolean);
    const first = (parts[0] || "").slice(0, 1);
    const last = (parts[parts.length - 1] || "").slice(0, 1);
    return (first + last).toUpperCase();
  }, [candidateName]);

  const applyCandidatePatch = useCallback((patch = {}) => {
    setCandidateLive((prev) =>
      normalizeCandidateDraftShape({
        ...(prev || {}),
        ...(patch || {}),
      })
    );
  }, []);

  const openAvatarPicker = useCallback(() => {
    if (!candidateId) {
      setAvatarOps({ loading: false, error: "Candidate ID is missing.", success: "" });
      return;
    }
    avatarInputRef.current?.click();
  }, [candidateId]);

  const uploadCandidateAvatar = useCallback(
    async (file) => {
      if (!candidateId) throw new Error("Candidate ID missing.");

      const fd = new FormData();
      fd.append("file", file);

      const url = buildApiUrl(`/candidate-avatar/${candidateId}`);
      const res = await fetch(url, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: authHeaders(),
      });

      const data = await safeJsonOrNull(res);
      if (!res.ok) throw new Error(data?.error || `Avatar upload failed (${res.status})`);
      return data;
    },
    [candidateId]
  );

  const onAvatarSelected = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;

      setAvatarOps({ loading: true, error: "", success: "" });

      try {
        const data = await uploadCandidateAvatar(file);
const avatarUrl = makeAbsolutePublicFileUrl(
  data?.avatar_url ||
    data?.url ||
    data?.candidate?.avatar_url ||
    ""
);

        if (!avatarUrl) {
          throw new Error("Avatar uploaded but no avatar_url was returned.");
        }

      applyCandidatePatch({
        ...(data?.candidate || {}),
        avatar_url: avatarUrl,
      });

        setAvatarOps({
          loading: false,
          error: "",
          success: "Avatar uploaded.",
        });
      } catch (err) {
        setAvatarOps({
          loading: false,
          error: err?.message || "Failed to upload avatar.",
          success: "",
        });
      }
    },
    [uploadCandidateAvatar, applyCandidatePatch]
  );

  const updateCandidateHeaderField = useCallback((field, value) => {
    setCandidateLive((prev) => {
      const current = normalizeCandidateDraftShape(prev || {});
      const next = { ...current, [field]: value };

      const firstName = field === "first_name" ? safeString(value) : safeString(current?.first_name || "");
      const lastName = field === "last_name" ? safeString(value) : safeString(current?.last_name || "");

      const city = field === "city" ? safeString(value) : safeString(current?.city || "");
      const state = field === "state" ? safeString(value) : safeString(current?.state || "");

      next.first_name = firstName;
      next.last_name = lastName;
      next.full_name = [safeTrim(firstName), safeTrim(lastName)].filter(Boolean).join(" ").trim();
      next.name = next.full_name || current?.name || "";

      next.city = city;
      next.state = state;
      next.location = buildLocationLabel(city, state);

      if (field === "email") next.email = value;
      if (field === "phone") {
        next.phone = normalizePhoneValue(value);
        next.phone_number = normalizePhoneValue(value);
      }
      if (field === "address") next.address = value;
      if (field === "website") {
        next.website = value;
        next.website_url = value;
      }

      return normalizeCandidateDraftShape(next);
    });
  }, []);

  const updateExtraFieldAt = useCallback((fieldKey, index, value) => {
    setCandidateLive((prev) => {
      const current = normalizeCandidateDraftShape(prev || {});
      const currentArray = Array.isArray(current?.[fieldKey]) ? [...current[fieldKey]] : [];
      while (currentArray.length <= index) currentArray.push("");
      currentArray[index] = value;
      return normalizeCandidateDraftShape({ ...(current || {}), [fieldKey]: currentArray });
    });
  }, []);

  const addExtraField = useCallback((fieldKey) => {
    setCandidateLive((prev) => {
      const current = normalizeCandidateDraftShape(prev || {});
      const currentArray = Array.isArray(current?.[fieldKey]) ? [...current[fieldKey]] : [];
      return normalizeCandidateDraftShape({ ...(current || {}), [fieldKey]: [...currentArray, ""] });
    });
  }, []);

  const removeExtraFieldAt = useCallback((fieldKey, index) => {
    setCandidateLive((prev) => {
      const current = normalizeCandidateDraftShape(prev || {});
      const currentArray = Array.isArray(current?.[fieldKey]) ? [...current[fieldKey]] : [];
      const nextArray = currentArray.filter((_, i) => i !== index);
      return normalizeCandidateDraftShape({ ...(current || {}), [fieldKey]: nextArray });
    });
  }, []);

  const updateSocialLinkAt = useCallback((index, value) => {
    setCandidateLive((prev) => {
      const base = normalizeCandidateDraftShape(prev || {});
      const current = Array.isArray(base.social_links) ? [...base.social_links] : [];

      while (current.length <= index) current.push("");
      current[index] = value;

      const trimmedLinks = current.map((x) => safeTrim(x)).filter(Boolean);

      return normalizeCandidateDraftShape({
        ...base,
        social_links: current,
        socialLinks: current,
        social_links_text: trimmedLinks.join(", "),
        socialLinksText: trimmedLinks.join(", "),
      });
    });
  }, []);

  const addSocialLinkField = useCallback(() => {
    setCandidateLive((prev) => {
      const base = normalizeCandidateDraftShape(prev || {});
      const current = Array.isArray(base.social_links) ? [...base.social_links] : [];
      const nextLinks = [...current, ""];
      const trimmedLinks = nextLinks.map((x) => safeTrim(x)).filter(Boolean);

      return normalizeCandidateDraftShape({
        ...base,
        social_links: nextLinks,
        socialLinks: nextLinks,
        social_links_text: trimmedLinks.join(", "),
        socialLinksText: trimmedLinks.join(", "),
      });
    });
  }, []);

  const removeSocialLinkAt = useCallback((index) => {
    setCandidateLive((prev) => {
      const base = normalizeCandidateDraftShape(prev || {});
      const current = Array.isArray(base.social_links) ? [...base.social_links] : [];
      const nextLinks = current.filter((_, i) => i !== index);
      const trimmedLinks = nextLinks.map((x) => safeTrim(x)).filter(Boolean);

      return normalizeCandidateDraftShape({
        ...base,
        social_links: nextLinks,
        socialLinks: nextLinks,
        social_links_text: trimmedLinks.join(", "),
        socialLinksText: trimmedLinks.join(", "),
      });
    });
  }, []);

  const fetchCandidateDetails = useCallback(async () => {
    if (!candidateId) return;

    setCandidateLoad({ loading: true, error: "" });

    const endpoints = [
      `/candidates/${candidateId}`,
      `/candidates/${candidateId}/profile`,
      `/candidate/${candidateId}`,
      `/candidate/${candidateId}/profile`,
      `/candidates/${candidateId}/details`,
    ];

    try {
      for (const path of endpoints) {
        const url = buildApiUrl(path);
        const res = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers: authHeaders(),
        });

        if (!res.ok) continue;

        const data = await safeJsonOrNull(res);
        const row = data?.candidate || data?.data || data;

        if (row && (row.id || row.candidate_id || candidateId)) {
          const normalized = normalizeCandidateDraftShape({
            ...row,
            id: row.id || row.candidate_id || candidateId,
          });

setCandidateLive((prev) => {
  const prevDraft = normalizeCandidateDraftShape(prev || {});

  const mergedAvatarRaw =
    safeTrim(
      normalized?.avatar_url ||
        normalized?.avatar ||
        normalized?.photo_url ||
        normalized?.photo ||
        normalized?.profile_photo ||
        normalized?.image_url ||
        normalized?.image ||
        ""
    ) ||
    safeTrim(
      prevDraft?.avatar_url ||
        prevDraft?.avatar ||
        prevDraft?.photo_url ||
        prevDraft?.photo ||
        prevDraft?.profile_photo ||
        prevDraft?.image_url ||
        prevDraft?.image ||
        ""
    );

  const merged = {
    ...prevDraft,
    ...normalized,
    avatar_url: mergedAvatarRaw ? makeAbsolutePublicFileUrl(mergedAvatarRaw) : "",
    city:
      safeString(normalized?.city || "") ||
      safeString(prevDraft?.city || ""),
    state:
      safeString(normalized?.state || "") ||
      safeString(prevDraft?.state || ""),
    phone:
      safeString(normalized?.phone || normalized?.phone_number || "") ||
      safeString(prevDraft?.phone || prevDraft?.phone_number || ""),
    phone_number:
      safeString(normalized?.phone_number || normalized?.phone || "") ||
      safeString(prevDraft?.phone_number || prevDraft?.phone || ""),
    location:
      safeString(normalized?.location || "") ||
      buildLocationLabel(normalized?.city || "", normalized?.state || "") ||
      safeString(prevDraft?.location || ""),
    social_links:
      hasExplicitSocialLinks(normalized)
        ? Array.isArray(normalized?.social_links)
          ? normalized.social_links
          : getSocialLinksArray(normalized, { preserveEmpty: true })
        : Array.isArray(prevDraft?.social_links)
        ? prevDraft.social_links
        : [],
  };

  if ((!safeTrim(merged.city) || !safeTrim(merged.state)) && safeTrim(merged.location)) {
    const splitLocation = splitLocationToCityState(merged.location);
    if (!safeTrim(merged.city) && splitLocation.city) merged.city = splitLocation.city;
    if (!safeTrim(merged.state) && splitLocation.state) merged.state = splitLocation.state;
  }

  return normalizeCandidateDraftShape(merged);
});

          setCandidateLoad({ loading: false, error: "" });
          return;
        }
      }

      setCandidateLoad({
        loading: false,
        error:
          "Could not load candidate details from API. Your candidate GET endpoint is not returning the resume fields (experience_items, education_items, preferences_json, etc).",
      });
    } catch (err) {
      setCandidateLoad({ loading: false, error: err?.message || "Failed to load candidate details." });
    }
  }, [candidateId]);

  useEffect(() => {
    fetchCandidateDetails();
  }, [fetchCandidateDetails]);

  const uiModel = useMemo(
    () => buildUiModel({ candidate: candidateLive || candidate || {}, parsedRaw }),
    [candidateLive, candidate, parsedRaw]
  );

  const experienceItems = uiModel.experienceArr;
  const educationItems = uiModel.educationArr;
  const skillsArr = uiModel.skills;
  const skillsText = skillsArr.length ? skillsArr.join(", ") : "";
  const preferences = uiModel.preferences;
  const advancedPreferences = uiModel.advancedPreferences;
  const experienceCompanies = useMemo(
    () => Array.from(new Set(experienceItems.map((item) => safeTrim(item?.company || item?.company_name || item?.employer || "")).filter(Boolean))),
    [experienceItems]
  );

  const openExperienceCompanyScan = useCallback(async () => {
    setSelectedExperienceCompanies(experienceCompanies);
    setExistingExperienceCompanyNames(new Set());
    setCompanyScanState({ loading: false, saving: false, completed: 0, total: 0, companyName: "", error: "" });
    setCompanyScanDialogOpen(true);
    setCompanyExistenceLoading(true);
    try {
      const response = await fetch(buildApiUrl("/companies?limit=5000"), {
        credentials: "include",
        headers: authHeaders(),
      });
      const data = await safeJsonOrNull(response);
      if (!response.ok) throw new Error(data?.error || data?.message || "Unable to check existing companies.");
      const companies = Array.isArray(data) ? data : data?.companies || data?.data || [];
      setExistingExperienceCompanyNames(
        new Set(companies.map((company) => safeTrim(company?.name || company?.company_name).toLowerCase()).filter(Boolean))
      );
    } catch (error) {
      setCompanyScanState((state) => ({ ...state, error: error?.message || "Unable to check existing companies." }));
    } finally {
      setCompanyExistenceLoading(false);
    }
  }, [experienceCompanies]);

  const startExperienceCompanyScan = useCallback(async () => {
    const names = selectedExperienceCompanies.length ? selectedExperienceCompanies : experienceCompanies;
    if (!names.length) return;
    setCompanyScanState({ loading: true, saving: false, completed: 0, total: names.length, companyName: "", error: "" });
    setCompanyScanDialogOpen(false);
    setOpeningJobsDialogOpen(true);
    try {
      const companiesResponse = await fetch(buildApiUrl("/companies?limit=5000"), { credentials: "include", headers: authHeaders() });
      const companiesData = await safeJsonOrNull(companiesResponse);
      const companies = Array.isArray(companiesData) ? companiesData : companiesData?.companies || companiesData?.data || [];
      const rows = [];
      for (let index = 0; index < names.length; index += 1) {
        const name = names[index];
        setCompanyScanState((state) => ({ ...state, completed: index, companyName: name }));
        let company = companies.find((row) => safeTrim(row?.name || row?.company_name).toLowerCase() === name.toLowerCase());
        if (!company) {
          const createResponse = await fetch(buildApiUrl("/companies"), {
            method: "POST", credentials: "include",
            headers: authHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify({ name, company_name: name, source: "Candidate experience scan" }),
          });
          const created = await safeJsonOrNull(createResponse);
          if (!createResponse.ok) throw new Error(created?.error || created?.message || `Unable to create ${name}.`);
          company = created?.company || created?.data?.company || created?.data || created;
        }
        const companyId = company?.id || company?.company_id;
        if (companyId) {
          const previewResponse = await fetch(buildApiUrl(`/companies/${encodeURIComponent(companyId)}/career-jobs/preview`), {
            method: "POST", credentials: "include", headers: authHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify({ max_jobs: 100, max_pages: 20 }),
          });
          const preview = await safeJsonOrNull(previewResponse);
          if (previewResponse.ok) (Array.isArray(preview?.jobs) ? preview.jobs : []).forEach((job, jobIndex) => {
            if (safeTrim(job?.job_title || job?.title)) rows.push({ ...job, companyId, companyName: name, importTarget: getOpeningImportTarget(job), id: `${companyId}:${job?.id || job?.job_url || jobIndex}` });
          });
        }
        setCompanyScanState((state) => ({ ...state, completed: index + 1, companyName: name }));
      }
      setScannedOpeningJobs(rows);
      setSelectedOpeningJobIds([]);
      setOpeningJobsDialogOpen(true);
    } catch (error) {
      setCompanyScanState((state) => ({ ...state, error: error?.message || "Unable to scan selected companies." }));
      setOpeningJobsDialogOpen(false);
      setCompanyScanDialogOpen(true);
    } finally {
      setCompanyScanState((state) => ({ ...state, loading: false }));
    }
  }, [experienceCompanies, selectedExperienceCompanies]);

  const importSelectedOpeningJobs = useCallback(async () => {
    const jobs = scannedOpeningJobs.filter((job) => selectedOpeningJobIds.includes(job.id));
    if (!jobs.length) return;
    setCompanyScanState((state) => ({ ...state, saving: true, error: "" }));
    try {
      await Promise.all(Array.from(new Set(jobs.map((job) => `${job.companyId}:${job.importTarget}`))).map(async (groupKey) => {
        const [companyId, importTarget] = groupKey.split(":");
        const groupJobs = jobs.filter((job) => String(job.companyId) === companyId && job.importTarget === importTarget);
        const response = await fetch(buildApiUrl(`/companies/${encodeURIComponent(companyId)}/career-jobs/import`), {
          method: "POST", credentials: "include", headers: authHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ jobs: groupJobs, import_target: importTarget }),
        });
        const data = await safeJsonOrNull(response);
        if (!response.ok) throw new Error(data?.error || data?.message || "Unable to add selected opening jobs.");
      }));
      setOpeningJobsDialogOpen(false);
      setExpOps({ loading: false, error: "", success: "Selected openings were added as leads or jobs based on their source." });
    } catch (error) {
      setCompanyScanState((state) => ({ ...state, error: error?.message || "Unable to add selected opening jobs." }));
    } finally {
      setCompanyScanState((state) => ({ ...state, saving: false }));
    }
  }, [scannedOpeningJobs, selectedOpeningJobIds]);

  useEffect(() => {
    if (!isEditingProfile) return;

    setExpDraft(experienceItems);
    setEduDraft(educationItems);
    setSkillsDraft(skillsText || "");

    setPrefDraft({
      whyLooking: safeTrim(advancedPreferences?.whyLooking || ""),
      jobTypesCsv: (advancedPreferences?.jobTypes || []).join(", "),
      workModesCsv: (advancedPreferences?.workModes || []).join(", "),
      timeline: safeTrim(advancedPreferences?.timeline || ""),
      compensation: safeTrim(advancedPreferences?.compensation || ""),
      needSponsorship: safeTrim(advancedPreferences?.needSponsorship || ""),
      interviewAvailability: safeTrim(advancedPreferences?.interviewAvailability || ""),
      jobType: safeTrim(preferences?.jobType || "Unknown") || "Unknown",
      workMode: safeTrim(preferences?.workMode || "Unknown") || "Unknown",
      salaryRange: safeTrim(preferences?.salaryRange || ""),
      locationsCsv: (preferences?.locations || []).join(", "),
      locationsList: normalizeLocationFilterValues(preferences?.locations || []),
      availability: safeTrim(preferences?.availability || ""),
      notes: safeTrim(preferences?.notes || ""),
    });

    // The draft is intentionally initialized only when its backing record changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditingProfile,
    candidateLive?.id,
    candidateLive?.experience_items,
    candidateLive?.education_items,
    candidateLive?.skills,
    candidateLive?.preferences_json,
    candidateLive?.pref_job_type,
    candidateLive?.pref_work_mode,
    candidateLive?.pref_salary_range,
    candidateLive?.pref_locations,
    candidateLive?.pref_availability,
    candidateLive?.pref_why_looking,
    candidateLive?.pref_job_types,
    candidateLive?.pref_work_modes,
    candidateLive?.pref_timeline,
    candidateLive?.pref_compensation,
    candidateLive?.pref_need_sponsorship,
    candidateLive?.pref_interview_availability,
    parsedRaw,
  ]);

  const getFieldSx = useCallback(
    (editing = false) => ({
      width: "100%",
      "& .MuiOutlinedInput-root": {
        height: 42,
        borderRadius: "8px",
        backgroundColor: FIELD_BG,
        pointerEvents: editing ? "auto" : "none",
        transition: "box-shadow 0.16s ease, border-color 0.16s ease",
        boxShadow: editing ? "0 0 0 1px rgba(156,79,255,0.22)" : "none",
        "& fieldset": {
          borderColor: editing ? FIELD_BORDER : BORDER,
          borderWidth: 1,
        },
        "&:hover fieldset": { borderColor: editing ? PURPLE : BORDER },
        "&.Mui-focused fieldset": {
          borderColor: editing ? PURPLE : BORDER,
          borderWidth: 1,
        },
      },
      "& input": {
        fontSize: 13,
        color: "#111827",
        fontWeight: 400,
        cursor: editing ? "text" : "default",
        padding: "9px 12px",
      },
      "& .MuiSelect-select": {
        fontSize: 13,
        fontWeight: 400,
        padding: "9px 12px",
      },
    }),
    []
  );

  const getMultilineSx = useCallback(
    (editing = false) => ({
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        backgroundColor: FIELD_BG,
        pointerEvents: editing ? "auto" : "none",
        transition: "box-shadow 0.16s ease, border-color 0.16s ease",
        boxShadow: editing ? "0 0 0 1px rgba(156,79,255,0.22)" : "none",
        "& fieldset": {
          borderColor: editing ? FIELD_BORDER : BORDER,
          borderWidth: 1,
        },
        "&:hover fieldset": { borderColor: editing ? PURPLE : BORDER },
        "&.Mui-focused fieldset": {
          borderColor: editing ? PURPLE : BORDER,
          borderWidth: 1,
        },
      },
      "& textarea": {
        fontSize: 13,
        color: "#111827",
        fontWeight: 400,
        cursor: editing ? "text" : "default",
      },
    }),
    []
  );

  const fieldSx = getFieldSx(true);
  const multilineSx = getMultilineSx(true);
  const topFieldSx = getFieldSx(isEditingProfile);
  const topMultilineSx = {
    ...getMultilineSx(isEditingProfile),
    gridColumn: { xs: "1 / -1", md: "2 / 4" },
  };

  const labelTypo = PROFILE_LABEL_SX;

  const [documents, setDocuments] = useState([]);
  const storedResumeDocs = (() => {
    const docSources = [
      candidateLive?.documents_json,
      candidateLive?.documentsJson,
      candidate?.documents_json,
      candidate?.documentsJson,
    ];

    for (const source of docSources) {
      const docs = normalizeDocumentArrayInput(source);
      if (docs.length) return normalizeDocsFromApi(docs);
    }

    return [];
  })();

  const fallbackResumeDoc = useMemo(() => {
    const fallbackUrl = makeAbsolutePublicFileUrl(
      candidateLive?.resume_url || candidate?.resume_url || ""
    );

    if (!fallbackUrl) return null;

    return {
      id: "candidate-resume-fallback",
      filename: safeTrim(candidateLive?.resume_url || candidate?.resume_url || ""),
      name: "Resume",
      kind: "Resume",
      sizeLabel: "",
      url: fallbackUrl,
      raw: {},
    };
  }, [candidateLive, candidate]);

  const visibleDocuments = useMemo(() => {
    const merged = new Map();

    for (const doc of [...storedResumeDocs, ...documents, ...(fallbackResumeDoc ? [fallbackResumeDoc] : [])]) {
      if (!doc) continue;

      const normalizedUrl = makeAbsolutePublicFileUrl(doc?.url || "");
      if (!normalizedUrl) continue;

      const normalizedDoc = {
        ...doc,
        url: normalizedUrl,
      };

      const key = getDocumentMergeKey(normalizedDoc);
      if (!key) continue;

      const existing = merged.get(key);
      if (!existing || getDocumentQualityScore(normalizedDoc) > getDocumentQualityScore(existing)) {
        merged.set(key, normalizedDoc);
      }
    }

    return Array.from(merged.values());
  }, [storedResumeDocs, documents, fallbackResumeDoc]);

  const resumeDocuments = useMemo(
    () => visibleDocuments.filter((doc) => isResumeLikeDocument(doc)),
    [visibleDocuments]
  );

  const primaryResumeDoc = useMemo(
    () => resumeDocuments.find((doc) => hasUsablePreviewUrl(doc)) || null,
    [resumeDocuments]
  );

  function normalizeDocsFromApi(apiDocs) {
    const docs = Array.isArray(apiDocs) ? apiDocs : [];
    return docs.map((d, i) => {
      const filename =
        d.filename ||
        d.file_name ||
        d.stored_name ||
        d.storedFilename ||
        d.saved_name ||
        d.original_name ||
        d.name ||
        "";

      const rawUrl =
        d.url ||
        d.public_url ||
        d.download_url ||
        d.file_url ||
        (looksLikeStoredUploadReference(filename || d.name)
          ? buildCandidateResumeDocUrl(candidateId, filename || d.name)
          : "");
      const url = makeAbsolutePublicFileUrl(rawUrl);
      const id = filename || d.id || `doc-${i}-${Math.random().toString(16).slice(2)}`;

      return {
        id,
        filename,
        name: d.name || d.original_name || d.filename || filename || "Document",
        kind: d.kind || d.type || "Resume",
        sizeLabel: d.sizeLabel || (d.size ? bytesToLabel(d.size) : ""),
        url,
        raw: d,
      };
    });
  }

  const fetchCandidateDocuments = useCallback(async () => {
    if (!candidateId) return;

    setDocState({ loading: true, error: "" });

    try {
      const url = buildApiUrl(`/candidate-resume/${candidateId}/documents`);
      const res = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: authHeaders(),
      });

      const data = await safeJsonOrNull(res);
      if (!res.ok) throw new Error(data?.error || `Failed to load documents (${res.status})`);

      const normalized = normalizeDocsFromApi(data?.documents || []);
      setDocuments(normalized);
      setDocState({ loading: false, error: "" });

      setPreviewDoc((prev) => {
        if (!prev) return null;
        const match = normalized.find(
          (d) =>
            (d.id && prev.id && d.id === prev.id) ||
            (d.filename && prev.filename && d.filename === prev.filename) ||
            (d.url && prev.url && d.url === prev.url)
        );
        return match || null;
      });
    } catch (err) {
      setDocState({ loading: false, error: err?.message || "Failed to load documents." });
    }
  // normalizeDocsFromApi only depends on candidateId, which is listed here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  useEffect(() => {
    fetchCandidateDocuments();
  }, [fetchCandidateDocuments]);

  function getDocFilename(doc) {
    if (doc?.filename) return String(doc.filename).split("/").pop();
    if (doc?.url) {
      try {
        const u = new URL(doc.url, window.location.origin);
        const parts = u.pathname.split("/");
        return decodeURIComponent(parts[parts.length - 1] || "");
      } catch {
        const parts = String(doc.url).split("/");
        return decodeURIComponent(parts[parts.length - 1] || "");
      }
    }
    if (doc?.name) return String(doc.name).split("/").pop();
    return "";
  }

  const canRemoveDocument = useCallback(
    (doc) => {
      const normalizedUrl = makeAbsolutePublicFileUrl(doc?.url || "");
      if (!candidateId || !normalizedUrl) return false;
      return normalizedUrl.includes(`/uploads/${RESUME_UPLOAD_DIR}/${candidateId}/documents/`);
    },
    [candidateId]
  );

  const deleteCandidateResumeDoc = useCallback(
    async (doc) => {
      if (!candidateId) throw new Error("Candidate ID missing.");
      const filename = getDocFilename(doc);
      if (!filename) throw new Error("Could not determine filename for this document.");

      const url = buildApiUrl(`/candidate-resume/${candidateId}/documents/${encodeURIComponent(filename)}`);
      const res = await fetch(url, {
        method: "DELETE",
        credentials: "include",
        headers: authHeaders(),
      });

      const data = await safeJsonOrNull(res);
      if (!res.ok) throw new Error(data?.error || `Delete failed (${res.status})`);
      return data;
    },
    [candidateId]
  );

  const removeDoc = useCallback(
    async (doc) => {
      if (!doc) return;

      const filename = getDocFilename(doc);
      const ok = window.confirm(`Delete this resume?\n\n${filename || doc.name || "document"}`);
      if (!ok) return;

      setDeleteState({ loading: true, filename: filename || "", error: "" });
      setDocsOps({ loading: false, error: "", success: "" });

      try {
        await deleteCandidateResumeDoc(doc);
        setDocuments((prev) => prev.filter((d) => getDocFilename(d) !== filename));
        setDeleteState({ loading: false, filename: "", error: "" });
        setDocumentsDirty(true);

        setPreviewDoc((prev) => {
          if (!prev) return prev;
          const prevFile = getDocFilename(prev);
          return prevFile === filename ? null : prev;
        });
      } catch (err) {
        setDeleteState({ loading: false, filename: "", error: err?.message || "Failed to delete file." });
      }
    },
    [deleteCandidateResumeDoc]
  );

  const openDoc = useCallback(
    (doc) => {
      if (!doc) return;

      const normalizedUrl =
        makeAbsolutePublicFileUrl(doc?.url) ||
        buildCandidateResumeDocUrl(candidateId, getDocFilename(doc) || doc?.name || "");

      if (!normalizedUrl) return;

      setPreviewDoc({
        ...doc,
        url: normalizedUrl,
      });
    },
    [candidateId]
  );

  const closeInlinePreview = useCallback(() => {
    setPreviewDoc(null);
  }, []);

  const openFilePicker = useCallback(() => {
    if (!candidateId) {
      setDocState({ loading: false, error: "Candidate ID is missing. Save candidate first." });
      return;
    }
    fileInputRef.current?.click();
  }, [candidateId]);

  const openPrimaryResume = useCallback(() => {
    if (primaryResumeDoc) {
      openDoc(primaryResumeDoc);
      return;
    }

    openFilePicker();
  }, [openDoc, openFilePicker, primaryResumeDoc]);

  const uploadCandidateResume = useCallback(
    async (file) => {
      if (!candidateId) throw new Error("Candidate ID missing.");

      const fd = new FormData();
      fd.append("file", file);

      const url = buildApiUrl(`/upload/candidate-resume/${candidateId}`);
      const res = await fetch(url, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: authHeaders(),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Upload failed (${res.status})`);
      }

      return res.json();
    },
    [candidateId]
  );

  const saveParsedToDb = useCallback(
    async (parsedPayload) => {
      if (!candidateId) return;
      if (!parsedPayload) return;

      setSaveParsedState({ loading: true, error: "", success: "" });

      try {
        const normalizedParsed = normalizeCandidateDraftShape({
          ...parsedPayload,
          full_name:
            safeTrim(parsedPayload?.full_name || parsedPayload?.fullName || parsedPayload?.name || "") ||
            [
              safeTrim(parsedPayload?.first_name || parsedPayload?.firstName || ""),
              safeTrim(parsedPayload?.last_name || parsedPayload?.lastName || ""),
            ]
              .filter(Boolean)
              .join(" ")
              .trim(),
        });

        const url = buildApiUrl(`/candidate-resume/${candidateId}/save-parsed`);
        const res = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: authHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            parsed: {
              ...parsedPayload,
              first_name: normalizedParsed.first_name,
              last_name: normalizedParsed.last_name,
              full_name: normalizedParsed.full_name,
              phone: normalizedParsed.phone,
              city: normalizedParsed.city,
              state: normalizedParsed.state,
              location: normalizedParsed.location,
              address: normalizedParsed.address,
            },
          }),
        });

        const data = await safeJsonOrNull(res);
        if (!res.ok) throw new Error(data?.error || `Failed to save parsed data (${res.status})`);

        applyCandidatePatch({
          ...(data?.candidate || {}),
          first_name: normalizedParsed.first_name,
          last_name: normalizedParsed.last_name,
          full_name: normalizedParsed.full_name,
          phone: normalizedParsed.phone,
          phone_number: normalizedParsed.phone,
          city: normalizedParsed.city,
          state: normalizedParsed.state,
          location: normalizedParsed.location,
          address: normalizedParsed.address,
          experience_items: JSON.stringify(parsedPayload?.experience || []),
          education_items: JSON.stringify(parsedPayload?.education || []),
          skills: Array.isArray(parsedPayload?.skills)
            ? parsedPayload.skills.join(", ")
            : safeString(parsedPayload?.skills || ""),
          preferences_json: JSON.stringify(parsedPayload?.preferences || {}),
        });

        setParsedRaw(parsedPayload);
        setSaveParsedState({ loading: false, error: "", success: "Saved extracted resume data to database." });
      } catch (err) {
        setSaveParsedState({ loading: false, error: err?.message || "Failed to save parsed data.", success: "" });
      }
    },
    [candidateId, applyCandidatePatch]
  );

  const parseResumeWithAI = useCallback(
    async (file) => {
      if (!file) return null;
      setParseState({ loading: true, error: "" });
      try {
        const data = await mockParseResume(file);
        setParsedRaw(data);
        setParseState({ loading: false, error: "" });
        await saveParsedToDb(data);
        return data;
      } catch (err) {
        setParseState({ loading: false, error: err?.message || "Failed to parse resume." });
        return null;
      }
    },
    [saveParsedToDb]
  );

  const onFilesSelected = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      e.target.value = "";
      setDocState({ loading: true, error: "" });
      setDocsOps({ loading: false, error: "", success: "" });
      setPreviewDoc(null);
      setParseState({ loading: true, error: "" });
      setSaveParsedState({ loading: false, error: "", success: "" });

      try {
        let firstResponse = null;

        for (let i = 0; i < files.length; i++) {
          const uploadRes = await uploadCandidateResume(files[i]);
          if (i === 0) firstResponse = uploadRes;
        }

        if (firstResponse?.parsed) {
          const parsedNormalized = normalizeCandidateDraftShape({
            ...firstResponse.parsed,
            ...(firstResponse.candidate || {}),
          });

          setParsedRaw((prev) => ({
            ...(prev || {}),
            ...firstResponse.parsed,
            first_name: parsedNormalized.first_name,
            last_name: parsedNormalized.last_name,
            full_name: parsedNormalized.full_name,
            phone: parsedNormalized.phone,
            city: parsedNormalized.city,
            state: parsedNormalized.state,
            location: parsedNormalized.location,
            address: parsedNormalized.address,
          }));

          setSaveParsedState({
            loading: false,
            error: "",
            success: "Saved extracted resume data to database.",
          });

          applyCandidatePatch({
            ...(firstResponse?.candidate || {}),
            full_name:
              parsedNormalized.full_name ||
              safeTrim(firstResponse?.candidate?.full_name || firstResponse?.candidate?.name || ""),
            first_name:
              parsedNormalized.first_name ||
              safeString(firstResponse?.candidate?.first_name || ""),
            last_name:
              parsedNormalized.last_name ||
              safeString(firstResponse?.candidate?.last_name || ""),
            phone:
              parsedNormalized.phone ||
              normalizePhoneValue(firstResponse?.parsed?.phone || firstResponse?.parsed?.phone_number || "") ||
              normalizePhoneValue(firstResponse?.candidate?.phone || firstResponse?.candidate?.phone_number || ""),
            phone_number:
              parsedNormalized.phone ||
              normalizePhoneValue(firstResponse?.parsed?.phone || firstResponse?.parsed?.phone_number || "") ||
              normalizePhoneValue(firstResponse?.candidate?.phone || firstResponse?.candidate?.phone_number || ""),
            city:
              parsedNormalized.city ||
              safeString(firstResponse?.parsed?.city || "") ||
              safeString(firstResponse?.candidate?.city || ""),
            state:
              parsedNormalized.state ||
              safeString(firstResponse?.parsed?.state || "") ||
              safeString(firstResponse?.candidate?.state || ""),
            location:
              safeString(firstResponse?.candidate?.location || "") ||
              parsedNormalized.location ||
              safeString(firstResponse?.parsed?.location || ""),
            address:
              parsedNormalized.address ||
              safeString(firstResponse?.candidate?.address || "") ||
              safeString(firstResponse?.parsed?.address || ""),
            social_links: Array.isArray(firstResponse?.parsed?.social_links)
              ? firstResponse.parsed.social_links
              : getSocialLinksArray(candidateLive || {}, { preserveEmpty: true }),
            experience_items: JSON.stringify(firstResponse?.parsed?.experience || []),
            education_items: JSON.stringify(firstResponse?.parsed?.education || []),
            skills: Array.isArray(firstResponse?.parsed?.skills)
              ? firstResponse.parsed.skills.join(", ")
              : undefined,
            preferences_json: JSON.stringify(firstResponse?.parsed?.preferences || {}),
          });
        } else if (firstResponse?.candidate) {
          applyCandidatePatch({
            ...(firstResponse.candidate || {}),
          });
        } else {
          const parsed = await parseResumeWithAI(files[0]);
          if (parsed) {
            const parsedNormalized = normalizeCandidateDraftShape(parsed);
            applyCandidatePatch({
              full_name: parsedNormalized.full_name,
              first_name: parsedNormalized.first_name,
              last_name: parsedNormalized.last_name,
              phone: parsedNormalized.phone,
              phone_number: parsedNormalized.phone,
              city: parsedNormalized.city,
              state: parsedNormalized.state,
              location: parsedNormalized.location,
              address: parsedNormalized.address,
              experience_items: JSON.stringify(parsed?.experience || []),
              education_items: JSON.stringify(parsed?.education || []),
              skills: Array.isArray(parsed?.skills) ? parsed.skills.join(", ") : "",
              preferences_json: JSON.stringify(parsed?.preferences || {}),
            });
          }
        }

        setParseState({ loading: false, error: "" });

        await fetchCandidateDocuments();

        setDocState({ loading: false, error: "" });
        setDocumentsDirty(true);

        setTimeout(() => {
          setDocuments((prev) => {
            const firstUploadedName = files[0]?.name || "";
            const found =
              prev.find((d) => getDocFilename(d) === firstUploadedName) ||
              prev.find((d) => d.name === firstUploadedName) ||
              prev[0] ||
              null;
            if (found) {
              setPreviewDoc({
                ...found,
                url: makeAbsolutePublicFileUrl(found.url),
              });
            }
            return prev;
          });
        }, 0);
      } catch (err) {
        setParseState({ loading: false, error: err?.message || "Failed to parse resume." });
        setDocState({ loading: false, error: err?.message || "Upload failed." });
      }
    },
    [uploadCandidateResume, fetchCandidateDocuments, parseResumeWithAI, applyCandidatePatch, candidateLive]
  );

  const saveCandidateProfile = useCallback(async () => {
    if (!candidateId) {
      setProfileOps({ loading: false, error: "Candidate ID is missing.", success: "" });
      return false;
    }

    setProfileOps({ loading: true, error: "", success: "" });

    const normalizedLive = normalizeCandidateDraftShape(candidateLive || {});
    const fullNameFinal =
      safeTrim(normalizedLive?.full_name || "") ||
      [safeTrim(normalizedLive?.first_name || ""), safeTrim(normalizedLive?.last_name || "")]
        .filter(Boolean)
        .join(" ")
        .trim();

    const cityFinal = safeTrim(normalizedLive?.city || "");
    const stateFinal = safeTrim(normalizedLive?.state || "");
    const phoneFinal = safeTrim(normalizePhoneValue(normalizedLive?.phone || normalizedLive?.phone_number || ""));

    const socialLinksArrayValue = (
      Array.isArray(candidateLive?.social_links)
        ? candidateLive.social_links
        : getSocialLinksArray(candidateLive || {}, { preserveEmpty: true })
    )
      .map((x) => safeTrim(x))
      .filter(Boolean);

    const socialLinksTextValue = socialLinksArrayValue.join(", ");

    const extraEmails = getExtraEmailsArray(candidateLive || {});
    const extraPhones = getExtraPhonesArray(candidateLive || {});
    const extraWebsites = getExtraWebsitesArray(candidateLive || {});

    const profilePayload = {
      id: candidateId,
      candidate_id: candidateId,
      first_name: safeTrim(normalizedLive?.first_name || ""),
      last_name: safeTrim(normalizedLive?.last_name || ""),
      full_name: fullNameFinal,
      name: fullNameFinal,
      title: safeTrim(normalizedLive?.title || normalizedLive?.candidate_title || ""),
      candidate_title: safeTrim(normalizedLive?.candidate_title || normalizedLive?.title || ""),
      email: safeTrim(normalizedLive?.email || ""),
      phone: phoneFinal,
      phone_number: phoneFinal,
      website: safeTrim(normalizedLive?.website || normalizedLive?.website_url || ""),
      website_url: safeTrim(normalizedLive?.website_url || normalizedLive?.website || ""),
      company: safeTrim(
        normalizedLive?.company ||
          normalizedLive?.candidate_company ||
          normalizedLive?.current_company ||
          normalizedLive?.company_client ||
          ""
      ),
      candidate_company: safeTrim(
        normalizedLive?.candidate_company ||
          normalizedLive?.company ||
          normalizedLive?.current_company ||
          normalizedLive?.company_client ||
          ""
      ),
      current_company: safeTrim(
        normalizedLive?.current_company ||
          normalizedLive?.company ||
          normalizedLive?.candidate_company ||
          normalizedLive?.company_client ||
          ""
      ),
      company_client: safeTrim(
        normalizedLive?.company_client ||
          normalizedLive?.current_company ||
          normalizedLive?.company ||
          normalizedLive?.candidate_company ||
          ""
      ),
      city: cityFinal,
      state: stateFinal,
      location: buildLocationLabel(cityFinal, stateFinal),
      address: safeTrim(normalizedLive?.address || ""),
      social_links: socialLinksArrayValue,
      socialLinks: socialLinksArrayValue,
      social_links_text: socialLinksTextValue,
      socialLinksText: socialLinksTextValue,
      extra_emails: extraEmails,
      extraEmails,
      extra_phones: extraPhones,
      extraPhones,
      extra_websites: extraWebsites,
      extraWebsites,
      avatar_url: safeTrim(normalizedLive?.avatar_url || ""),
    };

    const requestBodies = [
      { ...profilePayload },
      { candidate: { ...profilePayload } },
      { profile: { ...profilePayload } },
      { data: { ...profilePayload } },
      { candidateId, ...profilePayload },
      { candidateId, candidate: { ...profilePayload } },
      { id: candidateId, profile: { ...profilePayload } },
    ];

    const endpoints = [
      { method: "PUT", path: `/candidates/${candidateId}` },
      { method: "PATCH", path: `/candidates/${candidateId}` },
      { method: "PUT", path: `/candidates/${candidateId}/profile` },
      { method: "PATCH", path: `/candidates/${candidateId}/profile` },
      { method: "PUT", path: `/candidate/${candidateId}` },
      { method: "PATCH", path: `/candidate/${candidateId}` },
      { method: "PUT", path: `/candidate/${candidateId}/profile` },
      { method: "PATCH", path: `/candidate/${candidateId}/profile` },
      { method: "PUT", path: `/candidates/${candidateId}/details` },
      { method: "PATCH", path: `/candidates/${candidateId}/details` },
    ];

    try {
      for (const endpoint of endpoints) {
        for (const body of requestBodies) {
          const res = await fetch(buildApiUrl(endpoint.path), {
            method: endpoint.method,
            credentials: "include",
            headers: authHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify(body),
          });

          const data = await safeJsonOrNull(res);
          if (!res.ok) continue;

          const returnedCandidate =
            data?.candidate ||
            data?.data ||
            data?.item ||
            (data && typeof data === "object" ? data : null);

          applyCandidatePatch({
            ...profilePayload,
            ...(returnedCandidate || {}),
            social_links: socialLinksArrayValue,
            socialLinks: socialLinksArrayValue,
            social_links_text: socialLinksTextValue,
            socialLinksText: socialLinksTextValue,
          });

          setProfileOps({ loading: false, error: "", success: "Candidate profile saved." });
          return true;
        }
      }

      throw new Error(
        "Could not save candidate profile. The frontend tried PUT/PATCH on /candidates/:id and /candidates/:id/profile, but none returned success."
      );
    } catch (err) {
      setProfileOps({ loading: false, error: err?.message || "Failed to save candidate profile.", success: "" });
      return false;
    }
  }, [candidateId, candidateLive, applyCandidatePatch]);

  const handleTopSaveClick = useCallback(async () => {
    const didSave = await saveCandidateProfile();
    if (didSave) {
      setActiveEditSection("");
      setIsEditingProfile(false);
    }
  }, [saveCandidateProfile]);

  const handleTopEditClick = useCallback(() => {
    if (profileOps.loading) return;
    resetOpsState(setProfileOps);
    setActiveEditSection("");
    setIsEditingProfile(true);
  }, [profileOps.loading]);

  const addNewExperience = useCallback(async () => {
    if (!candidateId) return;

    setExpOps({ loading: true, error: "", success: "" });
    try {
      const url = buildApiUrl(`/candidates/${candidateId}/experience-items`);
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          item: {
            id: makeId("exp"),
            jobTitle: "",
            company: "",
            location: "",
            from: "",
            to: "",
            summary: "",
            bullets: [],
          },
        }),
      });

      const data = await safeJsonOrNull(res);
      if (!res.ok) throw new Error(data?.error || `Failed to add experience (${res.status})`);

      const items = Array.isArray(data?.items) ? data.items : [];
      const currentExperience = getCurrentExperienceSnapshot(items);
      setExpDraft(items);
      applyCandidatePatch({
        experience_items: JSON.stringify(items),
        title: currentExperience.title,
        candidate_title: currentExperience.title,
        current_company: currentExperience.company,
        company: currentExperience.company,
        candidate_company: currentExperience.company,
        company_client: currentExperience.company,
      });

      setExpOps({ loading: false, error: "", success: "Added new experience entry." });
    } catch (err) {
      setExpOps({ loading: false, error: err?.message || "Failed to add experience entry.", success: "" });
    }
  }, [candidateId, applyCandidatePatch]);

  const deleteExperience = useCallback(
    async (itemId) => {
      if (!candidateId || !itemId) return;

      setExpOps({ loading: true, error: "", success: "" });
      try {
        const url = buildApiUrl(`/candidates/${candidateId}/experience-items/${encodeURIComponent(itemId)}`);
        const res = await fetch(url, {
          method: "DELETE",
          credentials: "include",
          headers: authHeaders(),
        });

        const data = await safeJsonOrNull(res);
        if (!res.ok) throw new Error(data?.error || `Failed to delete experience (${res.status})`);

        const items = Array.isArray(data?.items) ? data.items : [];
        const currentExperience = getCurrentExperienceSnapshot(items);
        setExpDraft(items);
        applyCandidatePatch({
          experience_items: JSON.stringify(items),
          title: currentExperience.title,
          candidate_title: currentExperience.title,
          current_company: currentExperience.company,
          company: currentExperience.company,
          candidate_company: currentExperience.company,
          company_client: currentExperience.company,
        });

        setExpOps({ loading: false, error: "", success: "Deleted experience entry." });
      } catch (err) {
        setExpOps({ loading: false, error: err?.message || "Failed to delete experience entry.", success: "" });
      }
    },
    [candidateId, applyCandidatePatch]
  );

  const saveExperience = useCallback(async () => {
    if (!candidateId) return;

    setExpOps({ loading: true, error: "", success: "" });

    try {
      const url = buildApiUrl(`/candidates/${candidateId}/experience-items`);
      const res = await fetch(url, {
        method: "PUT",
        credentials: "include",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ items: expDraft }),
      });

      const data = await safeJsonOrNull(res);
      if (!res.ok) throw new Error(data?.error || `Failed to save experience (${res.status})`);

      const items = Array.isArray(data?.items) ? data.items : expDraft;

      const currentExperience = getCurrentExperienceSnapshot(items);
      setExpDraft(items);
      applyCandidatePatch({
        experience_items: JSON.stringify(items),
        title: currentExperience.title,
        candidate_title: currentExperience.title,
        current_company: currentExperience.company,
        company: currentExperience.company,
        candidate_company: currentExperience.company,
        company_client: currentExperience.company,
      });
      setActiveEditSection((prev) => (prev === "experience" ? "" : prev));

      setExpOps({ loading: false, error: "", success: "Saved experience." });
    } catch (err) {
      setExpOps({ loading: false, error: err?.message || "Failed to save experience.", success: "" });
    }
  }, [candidateId, expDraft, applyCandidatePatch]);

  const addNewEducation = useCallback(async () => {
    if (!candidateId) return;

    setEduOps({ loading: true, error: "", success: "" });
    try {
      const url = buildApiUrl(`/candidates/${candidateId}/education-items`);
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          item: {
            id: makeId("edu"),
            school: "",
            degree: "",
            field: "",
            from: "",
            to: "",
            summary: "",
          },
        }),
      });

      const data = await safeJsonOrNull(res);
      if (!res.ok) throw new Error(data?.error || `Failed to add education (${res.status})`);

      const items = Array.isArray(data?.items) ? data.items : [];
      setEduDraft(items);
      applyCandidatePatch({ education_items: JSON.stringify(items) });

      setEduOps({ loading: false, error: "", success: "Added new education entry." });
    } catch (err) {
      setEduOps({ loading: false, error: err?.message || "Failed to add education entry.", success: "" });
    }
  }, [candidateId, applyCandidatePatch]);

  const deleteEducation = useCallback(
    async (itemId) => {
      if (!candidateId || !itemId) return;

      setEduOps({ loading: true, error: "", success: "" });
      try {
        const url = buildApiUrl(`/candidates/${candidateId}/education-items/${encodeURIComponent(itemId)}`);
        const res = await fetch(url, {
          method: "DELETE",
          credentials: "include",
          headers: authHeaders(),
        });

        const data = await safeJsonOrNull(res);
        if (!res.ok) throw new Error(data?.error || `Failed to delete education (${res.status})`);

        const items = Array.isArray(data?.items) ? data.items : [];
        setEduDraft(items);
        applyCandidatePatch({ education_items: JSON.stringify(items) });

        setEduOps({ loading: false, error: "", success: "Deleted education entry." });
      } catch (err) {
        setEduOps({ loading: false, error: err?.message || "Failed to delete education entry.", success: "" });
      }
    },
    [candidateId, applyCandidatePatch]
  );

  const saveEducation = useCallback(async () => {
    if (!candidateId) return;

    setEduOps({ loading: true, error: "", success: "" });

    try {
      const url = buildApiUrl(`/candidates/${candidateId}/education-items`);
      const res = await fetch(url, {
        method: "PUT",
        credentials: "include",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ items: eduDraft }),
      });

      const data = await safeJsonOrNull(res);
      if (!res.ok) throw new Error(data?.error || `Failed to save education (${res.status})`);

      const items = Array.isArray(data?.items) ? data.items : eduDraft;
      setEduDraft(items);
      applyCandidatePatch({ education_items: JSON.stringify(items) });
      setActiveEditSection((prev) => (prev === "education" ? "" : prev));

      setEduOps({ loading: false, error: "", success: "Saved education." });
    } catch (err) {
      setEduOps({ loading: false, error: err?.message || "Failed to save education.", success: "" });
    }
  }, [candidateId, eduDraft, applyCandidatePatch]);

  const saveSkills = useCallback(async () => {
    if (!candidateId) return;

    setSkillsOps({ loading: true, error: "", success: "" });

    try {
      const url = buildApiUrl(`/candidates/${candidateId}/skills`);
      const res = await fetch(url, {
        method: "PUT",
        credentials: "include",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ skills: skillsDraft || "" }),
      });

      const data = await safeJsonOrNull(res);
      if (!res.ok) throw new Error(data?.error || `Failed to save skills (${res.status})`);

      const skillsSaved = data?.skills ?? skillsDraft ?? "";
      setSkillsDraft(skillsSaved);
      applyCandidatePatch({ skills: skillsSaved });
      setActiveEditSection((prev) => (prev === "skills" ? "" : prev));

      setSkillsOps({ loading: false, error: "", success: "Saved skills." });
    } catch (err) {
      setSkillsOps({ loading: false, error: err?.message || "Failed to save skills.", success: "" });
    }
  }, [candidateId, skillsDraft, applyCandidatePatch]);

  const savePreferences = useCallback(async () => {
    if (!candidateId) return;

    setPrefOps({ loading: true, error: "", success: "" });

    try {
      const url = buildApiUrl(`/candidates/${candidateId}/preferences`);
      const payload = {
        preferences: {
          whyLooking: prefDraft.whyLooking || "",
          jobTypes: normalizeCsvToArray(prefDraft.jobTypesCsv),
          workModes: normalizeCsvToArray(prefDraft.workModesCsv),
          timeline: prefDraft.timeline || "",
          compensation: prefDraft.compensation || "",
          needSponsorship: prefDraft.needSponsorship || "",
          interviewAvailability: prefDraft.interviewAvailability || "",
          pref_job_type: prefDraft.jobType || "",
          pref_work_mode: prefDraft.workMode || "",
          salaryRange: prefDraft.salaryRange || "",
          availability: prefDraft.availability || "",
          pref_locations: normalizeLocationFilterValues(prefDraft.locationsList || []),
          notes: prefDraft.notes || "",
        },
      };

      const res = await fetch(url, {
        method: "PUT",
        credentials: "include",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });

      const data = await safeJsonOrNull(res);
      if (!res.ok) throw new Error(data?.error || `Failed to save preferences (${res.status})`);

      const localPreferenceObject = {
        whyLooking: prefDraft.whyLooking || "",
        jobTypes: normalizeCsvToArray(prefDraft.jobTypesCsv),
        workModes: normalizeCsvToArray(prefDraft.workModesCsv),
        timeline: prefDraft.timeline || "",
        compensation: prefDraft.compensation || "",
        needSponsorship: prefDraft.needSponsorship || "",
        interviewAvailability: prefDraft.interviewAvailability || "",
        jobType: prefDraft.jobType || "",
        workMode: prefDraft.workMode || "",
        salaryRange: prefDraft.salaryRange || "",
        availability: prefDraft.availability || "",
        locations: normalizeLocationFilterValues(prefDraft.locationsList || []),
        notes: prefDraft.notes || "",
      };

      applyCandidatePatch({
        ...(data?.candidate || {}),
        preferences_json: JSON.stringify(localPreferenceObject),
        pref_why_looking: prefDraft.whyLooking || "",
        pref_job_types: JSON.stringify(normalizeCsvToArray(prefDraft.jobTypesCsv)),
        pref_work_modes: JSON.stringify(normalizeCsvToArray(prefDraft.workModesCsv)),
        pref_timeline: prefDraft.timeline || "",
        pref_compensation: prefDraft.compensation || "",
        pref_need_sponsorship: prefDraft.needSponsorship || "",
        pref_interview_availability: prefDraft.interviewAvailability || "",
        pref_job_type: prefDraft.jobType || "",
        pref_work_mode: prefDraft.workMode || "",
        pref_salary_range: prefDraft.salaryRange || "",
        pref_locations: JSON.stringify(normalizeLocationFilterValues(prefDraft.locationsList || [])),
        pref_availability: prefDraft.availability || "",
      });

      setActiveEditSection((prev) => (prev === "preferences" ? "" : prev));
      setPrefOps({ loading: false, error: "", success: "Saved preferences." });
    } catch (err) {
      setPrefOps({ loading: false, error: err?.message || "Failed to save preferences.", success: "" });
    }
  }, [candidateId, prefDraft, applyCandidatePatch]);

  const saveDocumentsSection = useCallback(() => {
    setActiveEditSection("");
    setDocsOps({
      loading: false,
      error: "",
      success: documentsDirty ? "Saved documents." : "No document changes to save.",
    });
    setDocumentsDirty(false);
  }, [documentsDirty]);

  const handleAddNewResumeFromPreview = useCallback(() => {
    setPreviewDoc(null);
    openFilePicker();
  }, [openFilePicker]);

  const renderExperienceView = () => (
    <Box sx={{ mt: 2 }}>
      {experienceItems.length ? (
        experienceItems.map((item) => {
          const companyWebsiteHref = normalizeWebsiteHref(item.companyWebsite);

          return (
            <ViewCard key={item.id}>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }} noWrap>
                    {item.jobTitle || "Experience"}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 400, color: MUTED }} noWrap>
                    {item.company ? (
                      <Box
                        component={companyWebsiteHref ? "a" : "span"}
                        href={companyWebsiteHref || undefined}
                        target={companyWebsiteHref ? "_blank" : undefined}
                        rel={companyWebsiteHref ? "noreferrer noopener" : undefined}
                        sx={{
                          color: companyWebsiteHref ? PURPLE : "inherit",
                          cursor: companyWebsiteHref ? "pointer" : "default",
                          textDecoration: "none",
                          "&:hover": companyWebsiteHref
                            ? { textDecoration: "underline" }
                            : undefined,
                        }}
                      >
                        {item.company}
                      </Box>
                    ) : null}
                    {item.company && item.location ? " • " : ""}
                    {item.location || ""}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 11, color: MUTED, fontWeight: 400, whiteSpace: "nowrap" }}>
                  {[item.from, item.to].filter(Boolean).join(" - ")}
                </Typography>
              </Box>

              {item.summary ? (
                <Typography sx={{ fontSize: 12, color: "#111827", fontWeight: 400, mt: 1 }}>
                  {item.summary}
                </Typography>
              ) : null}

              {item.bullets?.length ? (
                <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.25 }}>
                  {item.bullets.map((b, idx) => (
                    <Typography
                      key={idx}
                      component="li"
                      sx={{ fontSize: 12, color: "#111827", fontWeight: 400, mb: 0.5 }}
                    >
                      {b}
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </ViewCard>
          );
        })
      ) : (
        <EmptyBox height={120} />
      )}
    </Box>
  );

  const renderSkillsView = () => (
    <Box sx={{ mt: 2 }}>
      {skillsArr.length ? (
        <ViewCard>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {skillsArr.slice(0, 40).map((s, idx) => (
              <Chip
                key={`${s}-${idx}`}
                label={s}
                size="small"
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  bgcolor: "#F8FAFC",
                  border: `1px solid ${BORDER}`,
                }}
              />
            ))}
          </Box>
        </ViewCard>
      ) : (
        <EmptyBox height={120} />
      )}
    </Box>
  );

  const renderEducationView = () => (
    <Box sx={{ mt: 2 }}>
      {educationItems.length ? (
        educationItems.map((edu) => (
          <ViewCard key={edu.id}>
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }} noWrap>
                  {edu.school || "Education"}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 400, color: MUTED }} noWrap>
                  {[edu.degree, edu.field].filter(Boolean).join(" • ")}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: MUTED, fontWeight: 400, whiteSpace: "nowrap" }}>
                {[edu.from, edu.to].filter(Boolean).join(" - ")}
              </Typography>
            </Box>
          </ViewCard>
        ))
      ) : (
        <EmptyBox height={120} />
      )}
    </Box>
  );

  const renderPreferencesView = () => {
    const valueSx = {
      fontSize: 13,
      color: "#111827",
      fontWeight: 400,
      lineHeight: 1.45,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    };

    const jobTypeValues = advancedPreferences.jobTypes.length
      ? advancedPreferences.jobTypes
      : preferences.jobType && preferences.jobType !== "Unknown"
      ? [preferences.jobType]
      : [];
    const workModeValues = advancedPreferences.workModes.length
      ? advancedPreferences.workModes
      : preferences.workMode && preferences.workMode !== "Unknown"
      ? [preferences.workMode]
      : [];
    const timelineValue = advancedPreferences.timeline || preferences.availability || "";
    const compensationValue = advancedPreferences.compensation || preferences.salaryRange || "";
    const locationsValue = preferences.locations?.length ? preferences.locations.join(", ") : "";
    const notesValue = preferences.notes || "";
    const hasPreferenceData = Boolean(
      advancedPreferences.whyLooking ||
        jobTypeValues.length ||
        workModeValues.length ||
        timelineValue ||
        compensationValue ||
        advancedPreferences.needSponsorship ||
        advancedPreferences.interviewAvailability ||
        locationsValue ||
        notesValue
    );

    return (
    <Box sx={{ mt: 2 }}>
      {hasPreferenceData ? (
        <ViewCard>
          <PrefRow label="Why looking">
            <Box sx={{ pt: "10px" }}>
              <Typography sx={valueSx}>{advancedPreferences.whyLooking || "-"}</Typography>
            </Box>
          </PrefRow>

          <PrefRow label="Job Type / Workplace">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pt: "10px" }}>
              <Typography sx={valueSx}>Job Type: {jobTypeValues.length ? jobTypeValues.join(", ") : "-"}</Typography>
              <Typography sx={valueSx}>Workplace: {workModeValues.length ? workModeValues.join(", ") : "-"}</Typography>
            </Box>
          </PrefRow>

          <PrefRow label="Availability/Timeline">
            <Box sx={{ pt: "10px" }}>
              <Typography sx={valueSx}>{timelineValue || "-"}</Typography>
            </Box>
          </PrefRow>

          <PrefRow label="Compensation">
            <Box sx={{ pt: "10px" }}>
              <Typography sx={valueSx}>{compensationValue || "-"}</Typography>
            </Box>
          </PrefRow>

          <PrefRow label="Need Sponsorship">
            <Box sx={{ pt: "10px" }}>
              <Typography sx={valueSx}>{advancedPreferences.needSponsorship || "-"}</Typography>
            </Box>
          </PrefRow>

          <PrefRow label="Availability to Interview">
            <Box sx={{ pt: "10px" }}>
              <Typography sx={valueSx}>{advancedPreferences.interviewAvailability || "-"}</Typography>
            </Box>
          </PrefRow>

          {locationsValue ? (
            <PrefRow label="Locations">
              <Box sx={{ pt: "10px" }}>
                <Typography sx={valueSx}>{locationsValue}</Typography>
              </Box>
            </PrefRow>
          ) : null}

          {notesValue ? (
            <PrefRow label="Notes">
              <Box sx={{ pt: "10px" }}>
                <Typography sx={valueSx}>{notesValue}</Typography>
              </Box>
            </PrefRow>
          ) : null}
          {false && (
            <>
              <Box>
                <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>Salary Range</Typography>
              <Typography sx={{ fontSize: 13, color: "#111827", fontWeight: 400, mt: 0.25 }}>
                {preferences.salaryRange || "—"}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>Locations</Typography>
              <Typography sx={{ fontSize: 13, color: "#111827", fontWeight: 400, mt: 0.25 }}>
                {preferences.locations?.length ? preferences.locations.join(", ") : "—"}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>Availability</Typography>
              <Typography sx={{ fontSize: 13, color: "#111827", fontWeight: 400, mt: 0.25 }}>
                {preferences.availability || "—"}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>Notes</Typography>
              <Typography sx={{ fontSize: 13, color: "#111827", fontWeight: 400, mt: 0.25 }}>
                {preferences.notes || "—"}
              </Typography>
            </Box>
            </>
          )}
        </ViewCard>
      ) : (
        <EmptyBox height={120} />
      )}
    </Box>
    );
  };

  const renderDocumentsView = () => (
    <Box className="resume-files" sx={{ mt: 2 }}>
      {resumeDocuments.length ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {resumeDocuments.map((doc) => {
            const fn = getDocFilename(doc);
            return (
              <DocRow
                key={doc.id || fn}
                doc={doc}
                onRemove={removeDoc}
                onOpen={openDoc}
                removing={deleteState.loading && fn && deleteState.filename === fn}
                canRemove={canRemoveDocument(doc)}
              />
            );
          })}
        </Box>
      ) : (
        <EmptyBox height={120} />
      )}

      <Box sx={{ mt: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PlainPlusIcon />}
          onClick={openFilePicker}
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 400,
            color: PURPLE,
            borderColor: PURPLE,
            height: 40,
            minHeight: 40,
            px: 2.2,
            "&:hover": { borderColor: PURPLE },
            "& .MuiButton-startIcon": { mr: 1 },
          }}
        >
          Add More
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: PAGE_BG }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        multiple
        style={{ display: "none" }}
        onChange={onFilesSelected}
      />

      <input
        ref={avatarInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        style={{ display: "none" }}
        onChange={onAvatarSelected}
      />

      <ResumePreviewDialog
        open={!!previewDoc}
        doc={previewDoc}
        onClose={closeInlinePreview}
        onAddNew={handleAddNewResumeFromPreview}
      />

      <Box
        sx={{
          background: "#FFFFFF",
          borderRadius: 0,
          borderBottom: `1px solid ${BORDER}`,
          pb: 2,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "155px 1fr 1fr 140px 60px" },
            columnGap: { xs: 0, md: 4 },
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            onClick={
              !profileOps.loading
                ? isEditingProfile
                  ? handleTopSaveClick
                  : handleTopEditClick
                : undefined
            }
            sx={{
              gridColumn: { xs: "1 / -1", md: "5 / 6" },
              justifySelf: "end",
              fontSize: 12,
              color: PURPLE,
              fontWeight: 400,
              cursor: profileOps.loading ? "default" : "pointer",
              userSelect: "none",
              opacity: profileOps.loading ? 0.6 : 1,
              lineHeight: 1,
            }}
          >
            {profileOps.loading ? "Saving..." : isEditingProfile ? "Save" : "Edit"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "155px 1fr 1fr 140px 60px" },
            columnGap: { xs: 0, md: 4 },
            rowGap: 3,
            alignItems: "center",
            pt: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
               justifyContent:"space-between !important",
              gap: 0.85,
              minHeight: 40,
            }}
          >
            <Box sx={{ display: "flex",  flexDirection: "column", alignItems: "center", gap: 0.5 }}>
              <Avatar
                src={candidateAvatarSrc || undefined}
                alt={candidateName}
                onClick={openAvatarPicker}
                sx={{
                  width: 42,
                  height: 42,
                  fontSize: 11,
                  fontWeight: 400,
                  border: `1px solid ${BORDER}`,
                  bgcolor: "#F1F5F9",
                  color: "#0F172A",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    boxShadow: "0 0 0 3px rgba(156,79,255,0.12)",
                    borderColor: PURPLE,
                  },
                }}
                imgProps={{ referrerPolicy: "no-referrer" }}
              >
                {candidateInitials}
              </Avatar>

              <Typography
                onClick={openAvatarPicker}
                sx={{
                  fontSize: 11,
                  color: PURPLE,
                  fontWeight: 400,
                  cursor: "pointer",
                  userSelect: "none",
                  lineHeight: 1.2,
                }}
              >
                {avatarOps.loading ? "Uploading..." : "Upload avatar"}
              </Typography>
            </Box>

            <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>Name</Typography>
          </Box>

          <TextField
            size="small"
            value={candidateLive?.first_name || ""}
            onChange={(e) => updateCandidateHeaderField("first_name", e.target.value)}
            sx={topFieldSx}
          />
          <TextField
            size="small"
            value={candidateLive?.last_name || ""}
            onChange={(e) => updateCandidateHeaderField("last_name", e.target.value)}
            sx={topFieldSx}
          />

          <Button
            variant="contained"
            size="small"
            startIcon={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={openPrimaryResume}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 400,
              bgcolor: PURPLE,
              height: 40,
              minHeight: 40,
              px: 2.2,
              boxShadow: "none",
              "&:hover": { bgcolor: PURPLE, boxShadow: "none" },
              "& .MuiButton-startIcon": { mr: 1 },
            }}
          >
            Resume
          </Button>

          <Box sx={{ display: { xs: "none", md: "block" } }} />

          <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400, pt: "10px" }}>Email address</Typography>
          <Box sx={{ gridColumn: { xs: "1 / -1", md: "2 / 4" }, display: "grid", gap: 1 }}>
            <TextField
              size="small"
              value={candidateLive?.email || ""}
              onChange={(e) => updateCandidateHeaderField("email", e.target.value)}
              sx={topFieldSx}
            />

            {extraEmailsArray.map((value, index) => (
              <Box
                key={`extra-email-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 88px" },
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <TextField
                  size="small"
                  value={value}
                  onChange={(e) => updateExtraFieldAt("extra_emails", index, e.target.value)}
                  sx={topFieldSx}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!isEditingProfile}
                  onClick={() => removeExtraFieldAt("extra_emails", index)}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 400,
                    color: "#EF4444",
                    borderColor: "#EF4444",
                    height: 40,
                    minHeight: 40,
                    px: 2,
                    "&:hover": { borderColor: "#EF4444" },
                  }}
                >
                  Delete
                </Button>
              </Box>
            ))}
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<PlainPlusIcon />}
            disabled={!isEditingProfile}
            onClick={() => addExtraField("extra_emails")}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 400,
              color: PURPLE,
              borderColor: PURPLE,
              height: 40,
              minHeight: 40,
              px: 2.2,
              "&:hover": { borderColor: PURPLE },
              "& .MuiButton-startIcon": { mr: 1 },
            }}
          >
            Add More
          </Button>

          <Box sx={{ display: { xs: "none", md: "block" } }} />

          <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400, pt: "10px" }}>Title</Typography>
          <TextField
            size="small"
            value={candidateLive?.title || candidateLive?.candidate_title || ""}
            onChange={(e) => updateCandidateHeaderField("title", e.target.value)}
            sx={topFieldSx}
          />

          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "3 / 6" },
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "auto 280px" },
              gap: { xs: 1, md: 2 },
              alignItems: "center",
              justifyContent: "start",
            }}
          >
            <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400, pt: { xs: "10px", md: 0 } }}>
              Company
            </Typography>
            <TextField
              size="small"
              value={
                candidateLive?.current_company ||
                candidateLive?.company ||
                candidateLive?.candidate_company ||
                candidateLive?.company_client ||
                ""
              }
              onChange={(e) => updateCandidateHeaderField("current_company", e.target.value)}
              sx={topFieldSx}
            />
          </Box>

          <Box sx={{ display: { xs: "none", md: "block" } }} />

          <Typography
            sx={{
              gridColumn: { xs: "1 / -1", md: "1 / 2" },
              fontSize: 12,
              color: LABEL,
              fontWeight: 400,
              pt: "10px",
            }}
          >
            Phone Number
          </Typography>
          <Box sx={{ gridColumn: { xs: "1 / -1", md: "2 / 4" }, display: "grid", gap: 1 }}>
            <TextField
              size="small"
              value={candidateLive?.phone || candidateLive?.phone_number || ""}
              onChange={(e) => updateCandidateHeaderField("phone", e.target.value)}
              sx={topFieldSx}
            />

            {extraPhonesArray.map((value, index) => (
              <Box
                key={`extra-phone-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 88px" },
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <TextField
                  size="small"
                  value={value}
                  onChange={(e) => updateExtraFieldAt("extra_phones", index, e.target.value)}
                  sx={topFieldSx}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!isEditingProfile}
                  onClick={() => removeExtraFieldAt("extra_phones", index)}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 400,
                    color: "#EF4444",
                    borderColor: "#EF4444",
                    height: 40,
                    minHeight: 40,
                    px: 2,
                    "&:hover": { borderColor: "#EF4444" },
                  }}
                >
                  Delete
                </Button>
              </Box>
            ))}
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<PlainPlusIcon />}
            disabled={!isEditingProfile}
            onClick={() => addExtraField("extra_phones")}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 400,
              color: PURPLE,
              borderColor: PURPLE,
              height: 40,
              minHeight: 40,
              px: 2.2,
              "&:hover": { borderColor: PURPLE },
              "& .MuiButton-startIcon": { mr: 1 },
            }}
          >
            Add More
          </Button>

          <Box sx={{ display: { xs: "none", md: "block" } }} />

          <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400, pt: "10px" }}>Website</Typography>
          <Box sx={{ gridColumn: { xs: "1 / -1", md: "2 / 4" }, display: "grid", gap: 1 }}>
            <TextField
              size="small"
              value={candidateLive?.website || candidateLive?.website_url || ""}
              onChange={(e) => updateCandidateHeaderField("website", e.target.value)}
              sx={topFieldSx}
            />

            {extraWebsitesArray.map((value, index) => (
              <Box
                key={`extra-website-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 88px" },
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <TextField
                  size="small"
                  value={value}
                  onChange={(e) => updateExtraFieldAt("extra_websites", index, e.target.value)}
                  sx={topFieldSx}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!isEditingProfile}
                  onClick={() => removeExtraFieldAt("extra_websites", index)}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 400,
                    color: "#EF4444",
                    borderColor: "#EF4444",
                    height: 40,
                    minHeight: 40,
                    px: 2,
                    "&:hover": { borderColor: "#EF4444" },
                  }}
                >
                  Delete
                </Button>
              </Box>
            ))}
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<PlainPlusIcon />}
            disabled={!isEditingProfile}
            onClick={() => addExtraField("extra_websites")}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 400,
              color: PURPLE,
              borderColor: PURPLE,
              height: 40,
              minHeight: 40,
              px: 2.2,
              "&:hover": { borderColor: PURPLE },
              "& .MuiButton-startIcon": { mr: 1 },
            }}
          >
            Add More
          </Button>

          <Box sx={{ display: { xs: "none", md: "block" } }} />

          <Typography sx={{ ...labelTypo, alignSelf: "start", mt: 0.5 }}>Social Links</Typography>
          <Box sx={{ gridColumn: { xs: "1 / -1", md: "2 / 4" }, display: "grid", gap: 1 }}>
            {socialLinksArray.length ? (
              socialLinksArray.map((link, index) => (
                <Box
                  key={`social-link-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 88px" },
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <TextField
                    size="small"
                    type="url"
                    value={link}
                    onChange={(e) => updateSocialLinkAt(index, e.target.value)}
                    sx={topFieldSx}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!isEditingProfile}
                    onClick={() => removeSocialLinkAt(index)}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 400,
                      color: "#EF4444",
                      borderColor: "#EF4444",
                      height: 40,
                      minHeight: 40,
                      px: 2,
                      "&:hover": { borderColor: "#EF4444" },
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 88px" },
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <TextField
                  size="small"
                  value=""
                  onChange={(e) => updateSocialLinkAt(0, e.target.value)}
                  sx={topFieldSx}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!isEditingProfile}
                  onClick={() => removeSocialLinkAt(0)}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 400,
                    color: "#EF4444",
                    borderColor: "#EF4444",
                    height: 40,
                    minHeight: 40,
                    px: 2,
                    "&:hover": { borderColor: "#EF4444" },
                  }}
                >
                  Delete
                </Button>
              </Box>
            )}
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<PlainPlusIcon />}
            disabled={!isEditingProfile}
            onClick={addSocialLinkField}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 400,
              color: PURPLE,
              borderColor: PURPLE,
              height: 40,
              minHeight: 40,
              px: 2.2,
              "&:hover": { borderColor: PURPLE },
              "& .MuiButton-startIcon": { mr: 1 },
            }}
          >
            Add Link
          </Button>

          <Box sx={{ display: { xs: "none", md: "block" } }} />

          <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>Location</Typography>
          <TextField
            size="small"
            value={candidateLive?.city || ""}
            onChange={(e) => updateCandidateHeaderField("city", e.target.value)}
            sx={topFieldSx}
          />

          <TextField
            size="small"
            value={candidateLive?.state || ""}
            onChange={(e) => updateCandidateHeaderField("state", e.target.value)}
            sx={topFieldSx}
          />

          <Box sx={{ display: { xs: "none", md: "block" } }} />
          <Box sx={{ display: { xs: "none", md: "block" } }} />

          <Typography sx={{ ...labelTypo, alignSelf: "start", mt: 0.5 }}>Address</Typography>
          <TextField
            multiline
            minRows={2}
            size="small"
            value={candidateLive?.address || ""}
            onChange={(e) => updateCandidateHeaderField("address", e.target.value)}
            placeholder="Full address goes here"
            sx={topMultilineSx}
          />

          <Box sx={{ display: { xs: "none", md: "block" } }} />
          <Box sx={{ display: { xs: "none", md: "block" } }} />
        </Box>

        <Box sx={{ mt: 2 }}>
          <SectionOpsNotice
            state={profileOps}
            loadingText="Saving candidate profile…"
            onClearSuccess={() => setProfileOps((prev) => ({ ...prev, success: "" }))}
          />

          <SectionOpsNotice
            state={avatarOps}
            loadingText="Uploading avatar…"
            onClearSuccess={() => setAvatarOps((prev) => ({ ...prev, success: "" }))}
          />

          {candidateLoad.loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 12, color: MUTED }}>Loading candidate resume fields…</Typography>
            </Box>
          ) : null}

          {candidateLoad.error ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {candidateLoad.error}
            </Alert>
          ) : null}

          {docState.loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 12, color: MUTED }}>Loading documents…</Typography>
            </Box>
          ) : null}

          {saveParsedState.loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 12, color: MUTED }}>Saving extracted data to database…</Typography>
            </Box>
          ) : null}

          {deleteState.loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 12, color: MUTED }}>
                Deleting resume{deleteState.filename ? `: ${deleteState.filename}` : ""}…
              </Typography>
            </Box>
          ) : null}

          {docState.error ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {docState.error}
            </Alert>
          ) : null}

          {parseState.error ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {parseState.error}
            </Alert>
          ) : null}

          {saveParsedState.error ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {saveParsedState.error}
            </Alert>
          ) : null}

          <AutoHideSuccessAlert
            message={saveParsedState.success}
            onClear={() => setSaveParsedState((prev) => ({ ...prev, success: "" }))}
            mt={1}
          />

          {deleteState.error ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {deleteState.error}
            </Alert>
          ) : null}
        </Box>
      </Box>

      {!isEditingProfile ? (
        <>
          <Box sx={{ mb: 2 }}>
            <ExperienceSectionTitle
              onEdit={() => {
                setIsEditingProfile(true);
                setActiveEditSection("experience");
                resetOpsState(setExpOps);
              }}
              onScan={openExperienceCompanyScan}
              scanning={companyScanState.loading || companyScanState.saving}
            />
            <SectionOpsNotice
              state={expOps}
              loadingText="Updating Experience…"
              onClearSuccess={() => setExpOps((prev) => ({ ...prev, success: "" }))}
            />
            {renderExperienceView()}
          </Box>

          <Box sx={{ mb: 2 }}>
            <SectionTitle
              title="Skills"
              onEdit={() => {
                setIsEditingProfile(true);
                setActiveEditSection("skills");
                resetOpsState(setSkillsOps);
              }}
            />
            <SectionOpsNotice
              state={skillsOps}
              loadingText="Updating Skills…"
              onClearSuccess={() => setSkillsOps((prev) => ({ ...prev, success: "" }))}
            />
            {renderSkillsView()}
          </Box>

          <Box sx={{ mb: 2 }}>
            <SectionTitle
              title="Education"
              onEdit={() => {
                setIsEditingProfile(true);
                setActiveEditSection("education");
                resetOpsState(setEduOps);
              }}
            />
            <SectionOpsNotice
              state={eduOps}
              loadingText="Updating Education…"
              onClearSuccess={() => setEduOps((prev) => ({ ...prev, success: "" }))}
            />
            {renderEducationView()}
          </Box>

          <Box sx={{ mb: 2 }}>
            <SectionTitle
              title="Preferences"
              onEdit={() => {
                setIsEditingProfile(true);
                setActiveEditSection("preferences");
                resetOpsState(setPrefOps);
              }}
            />
            <SectionOpsNotice
              state={prefOps}
              loadingText="Updating Preferences…"
              onClearSuccess={() => setPrefOps((prev) => ({ ...prev, success: "" }))}
            />
            {renderPreferencesView()}
          </Box>

          <Box>
            <SectionTitle
              title="Documents"
              onEdit={() => {
                setIsEditingProfile(true);
                setActiveEditSection("documents");
                resetOpsState(setDocsOps);
              }}
            />
            <SectionOpsNotice
              state={docsOps}
              loadingText="Updating Documents…"
              onClearSuccess={() => setDocsOps((prev) => ({ ...prev, success: "" }))}
            />
            {renderDocumentsView()}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <SectionTitle
              title="Experience"
              onEdit={() => {
                if (activeEditSection === "experience") {
                  saveExperience();
                } else {
                  setActiveEditSection("experience");
                  resetOpsState(setExpOps);
                }
              }}
              rightText={activeEditSection === "experience" ? "Save" : "Edit"}
            />

            <SectionOpsNotice
              state={expOps}
              loadingText="Updating Experience…"
              onClearSuccess={() => setExpOps((prev) => ({ ...prev, success: "" }))}
            />

            {activeEditSection === "experience" ? (
              <>
                {(expDraft.length
                  ? expDraft
                  : [{ id: "exp-empty", jobTitle: "", company: "", location: "", from: "", to: "", summary: "" }]
                ).map((item) => (
                  <EditCard key={item.id}>
                    <FormRow
                      label="Title"
                      right={
                        <TextField
                          size="small"
                          value={[item.from, item.to].filter(Boolean).join(" - ")}
                          sx={fieldSx}
                          onChange={(e) => {
                            const v = e.target.value || "";
                            const parts = v.split("-").map((x) => x.trim());
                            const from = parts[0] || "";
                            const to = parts.slice(1).join(" - ").trim() || "";
                            setExpDraft((prev) => prev.map((p) => (p.id === item.id ? { ...p, from, to } : p)));
                          }}
                        />
                      }
                    >
                      <TextField
                        size="small"
                        value={item.jobTitle || ""}
                        sx={fieldSx}
                        onChange={(e) =>
                          setExpDraft((prev) =>
                            prev.map((p) => (p.id === item.id ? { ...p, jobTitle: e.target.value } : p))
                          )
                        }
                      />
                    </FormRow>

                    <FormRow label="Company, Location">
                      <TextField
                        size="small"
                        value={[item.company, item.location].filter(Boolean).join(" - ")}
                        sx={fieldSx}
                        onChange={(e) => {
                          const v = e.target.value || "";
                          const parts = v.split("-").map((x) => x.trim());
                          const company = parts[0] || "";
                          const location = parts.slice(1).join(" - ").trim() || "";
                          setExpDraft((prev) =>
                            prev.map((p) => (p.id === item.id ? { ...p, company, location } : p))
                          );
                        }}
                      />
                    </FormRow>

                    <FormRow label="Company URL">
                      <TextField
                        size="small"
                        type="url"
                        placeholder="https://company.com"
                        value={item.companyWebsite || item.company_website || item.company_link || ""}
                        sx={fieldSx}
                        onChange={(e) => {
                          const companyWebsite = e.target.value;
                          setExpDraft((prev) =>
                            prev.map((p) =>
                              p.id === item.id
                                ? {
                                    ...p,
                                    companyWebsite,
                                    company_website: companyWebsite,
                                    company_link: companyWebsite,
                                  }
                                : p
                            )
                          );
                        }}
                      />
                    </FormRow>

                    <FormRow label="Summary">
                      <TextField
                        multiline
                        minRows={3}
                        size="small"
                        value={item.summary || ""}
                        sx={multilineSx}
                        onChange={(e) =>
                          setExpDraft((prev) =>
                            prev.map((p) => (p.id === item.id ? { ...p, summary: e.target.value } : p))
                          )
                        }
                      />
                    </FormRow>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 0.5 }}>
                      <Typography
                        sx={{ fontSize: 12, color: PURPLE, fontWeight: 400, cursor: "pointer" }}
                        onClick={addNewExperience}
                      >
                        Add New
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: "#EF4444", fontWeight: 400, cursor: "pointer" }}
                        onClick={() => deleteExperience(item.id)}
                      >
                        Delete
                      </Typography>
                    </Box>
                  </EditCard>
                ))}
              </>
            ) : (
              renderExperienceView()
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <SectionTitle
              title="Skills"
              onEdit={() => {
                if (activeEditSection === "skills") {
                  saveSkills();
                } else {
                  setActiveEditSection("skills");
                  resetOpsState(setSkillsOps);
                }
              }}
              rightText={activeEditSection === "skills" ? "Save" : "Edit"}
            />

            <SectionOpsNotice
              state={skillsOps}
              loadingText="Updating Skills…"
              onClearSuccess={() => setSkillsOps((prev) => ({ ...prev, success: "" }))}
            />

            {activeEditSection === "skills" ? (
              <EditCard>
                <SubSectionHeader title="Tech skills" />
                <PopupMultiSelectField
                  options={fieldOptions.skills || []}
                  value={normalizeCsvToArray(skillsDraft)}
                  onChange={(nextValue) => setSkillsDraft(nextValue.join(", "))}
                  placeholder="Search or add skill"
                  inputSx={fieldSx}
                />
              </EditCard>
            ) : (
              renderSkillsView()
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <SectionTitle
              title="Education"
              onEdit={() => {
                if (activeEditSection === "education") {
                  saveEducation();
                } else {
                  setActiveEditSection("education");
                  resetOpsState(setEduOps);
                }
              }}
              rightText={activeEditSection === "education" ? "Save" : "Edit"}
            />

            <SectionOpsNotice
              state={eduOps}
              loadingText="Updating Education…"
              onClearSuccess={() => setEduOps((prev) => ({ ...prev, success: "" }))}
            />

            {activeEditSection === "education" ? (
              <>
                {(eduDraft.length
                  ? eduDraft
                  : [{ id: "edu-empty", school: "", degree: "", field: "", from: "", to: "", summary: "" }]
                ).map((edu) => (
                  <EditCard key={edu.id}>
                    <FormRow
                      label="Area, Degree"
                      right={
                        <TextField
                          size="small"
                          value={[edu.from, edu.to].filter(Boolean).join(" - ")}
                          sx={fieldSx}
                          onChange={(e) => {
                            const v = e.target.value || "";
                            const parts = v.split("-").map((x) => x.trim());
                            const from = parts[0] || "";
                            const to = parts.slice(1).join(" - ").trim() || "";
                            setEduDraft((prev) => prev.map((p) => (p.id === edu.id ? { ...p, from, to } : p)));
                          }}
                        />
                      }
                    >
                      <TextField
                        size="small"
                        value={[edu.field, edu.degree].filter(Boolean).join(" - ")}
                        sx={fieldSx}
                        onChange={(e) => {
                          const v = e.target.value || "";
                          const parts = v.split("-").map((x) => x.trim());
                          const field = parts[0] || "";
                          const degree = parts.slice(1).join(" - ").trim() || "";
                          setEduDraft((prev) => prev.map((p) => (p.id === edu.id ? { ...p, field, degree } : p)));
                        }}
                      />
                    </FormRow>

                    <FormRow label="School, University, Location">
                      <TextField
                        size="small"
                        value={edu.school || ""}
                        sx={fieldSx}
                        onChange={(e) =>
                          setEduDraft((prev) =>
                            prev.map((p) => (p.id === edu.id ? { ...p, school: e.target.value } : p))
                          )
                        }
                      />
                    </FormRow>

                    <FormRow label="Summary">
                      <TextField
                        multiline
                        minRows={3}
                        size="small"
                        value={edu.summary || ""}
                        sx={multilineSx}
                        onChange={(e) =>
                          setEduDraft((prev) =>
                            prev.map((p) => (p.id === edu.id ? { ...p, summary: e.target.value } : p))
                          )
                        }
                      />
                    </FormRow>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 0.5 }}>
                      <Typography
                        sx={{ fontSize: 12, color: PURPLE, fontWeight: 400, cursor: "pointer" }}
                        onClick={addNewEducation}
                      >
                        Add New
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: "#EF4444", fontWeight: 400, cursor: "pointer" }}
                        onClick={() => deleteEducation(edu.id)}
                      >
                        Delete
                      </Typography>
                    </Box>
                  </EditCard>
                ))}
              </>
            ) : (
              renderEducationView()
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <SectionTitle
              title="Preferences"
              onEdit={() => {
                if (activeEditSection === "preferences") {
                  savePreferences();
                } else {
                  setActiveEditSection("preferences");
                  resetOpsState(setPrefOps);
                }
              }}
              rightText={activeEditSection === "preferences" ? "Save" : "Edit"}
            />

            <SectionOpsNotice
              state={prefOps}
              loadingText="Updating Preferences…"
              onClearSuccess={() => setPrefOps((prev) => ({ ...prev, success: "" }))}
            />

            {activeEditSection === "preferences" ? (
              <EditCard sx={{ p: 2 }}>
                {(() => {
                  const jobTypesArr = normalizeCsvToArray(prefDraft.jobTypesCsv);
                  const workModesArr = normalizeCsvToArray(prefDraft.workModesCsv);

                  return (
                    <Box>
                      <PrefRow label="Why looking">
                        <TextField
                          multiline
                          minRows={6}
                          size="small"
                          value={prefDraft.whyLooking}
                          sx={{
                            ...multilineSx,
                            "& textarea": { fontSize: 13, color: "#111827", fontWeight: 400, lineHeight: 1.45 },
                          }}
                          onChange={(e) => setPrefDraft((p) => ({ ...p, whyLooking: e.target.value }))}
                        />
                      </PrefRow>

                      <PrefRow label="Job Type / Workplace">
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            columnGap: 4,
                            rowGap: 1.5,
                            pt: 0.25,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 120, columnGap: "15px" }}>
                            <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>Full-time:</Typography>
                            <SquareToggle
                              checked={jobTypesArr.includes("Full-time")}
                              onClick={() =>
                                setPrefDraft((p) => ({ ...p, jobTypesCsv: toggleInCsv(p.jobTypesCsv, "Full-time") }))
                              }
                            />
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 120 }}>
                            <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>Contract:</Typography>
                            <SquareToggle
                              checked={jobTypesArr.includes("Contract")}
                              onClick={() =>
                                setPrefDraft((p) => ({ ...p, jobTypesCsv: toggleInCsv(p.jobTypesCsv, "Contract") }))
                              }
                            />
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 140 }}>
                            <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>On-site:</Typography>
                            <SquareToggle
                              checked={workModesArr.includes("On-site")}
                              onClick={() =>
                                setPrefDraft((p) => ({ ...p, workModesCsv: toggleInCsv(p.workModesCsv, "On-site") }))
                              }
                            />
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 140 }}>
                            <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>Hybrid:</Typography>
                            <SquareToggle
                              checked={workModesArr.includes("Hybrid")}
                              onClick={() =>
                                setPrefDraft((p) => ({ ...p, workModesCsv: toggleInCsv(p.workModesCsv, "Hybrid") }))
                              }
                            />
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 140 }}>
                            <Typography sx={{ fontSize: 12, color: LABEL, fontWeight: 400 }}>Remote:</Typography>
                            <SquareToggle
                              checked={workModesArr.includes("Remote")}
                              onClick={() =>
                                setPrefDraft((p) => ({ ...p, workModesCsv: toggleInCsv(p.workModesCsv, "Remote") }))
                              }
                            />
                          </Box>
                        </Box>
                      </PrefRow>

                      <PrefRow label="Availability/Timeline">
                        <Box sx={{ maxWidth: 520 }}>
                          <TextField
                            size="small"
                            value={prefDraft.timeline}
                            sx={fieldSx}
                            onChange={(e) => setPrefDraft((p) => ({ ...p, timeline: e.target.value }))}
                          />
                        </Box>
                      </PrefRow>

                      <PrefRow label="Compensation">
                        <Box sx={{ maxWidth: 520 }}>
                          <TextField
                            size="small"
                            value={prefDraft.compensation}
                            sx={fieldSx}
                            onChange={(e) => setPrefDraft((p) => ({ ...p, compensation: e.target.value }))}
                          />
                        </Box>
                      </PrefRow>

                      <PrefRow label="Need Sponsorship">
                        <Box sx={{ maxWidth: 220 }}>
                          <TextField
                            select
                            size="small"
                            value={prefDraft.needSponsorship}
                            sx={fieldSx}
                            onChange={(e) => setPrefDraft((p) => ({ ...p, needSponsorship: e.target.value }))}
                          >
                            <MenuItem value="" sx={{ fontWeight: 400 }} />
                            <MenuItem value="YES" sx={{ fontWeight: 400 }}>
                              YES
                            </MenuItem>
                            <MenuItem value="NO" sx={{ fontWeight: 400 }}>
                              NO
                            </MenuItem>
                            <MenuItem value="NOT SURE" sx={{ fontWeight: 400 }}>
                              NOT SURE
                            </MenuItem>
                          </TextField>
                        </Box>
                      </PrefRow>

                      <PrefRow label="Availability to Interview">
                        <Box sx={{ maxWidth: 520 }}>
                          <TextField
                            size="small"
                            value={prefDraft.interviewAvailability}
                            sx={fieldSx}
                            onChange={(e) => setPrefDraft((p) => ({ ...p, interviewAvailability: e.target.value }))}
                          />
                        </Box>
                      </PrefRow>

                      <PrefRow label="Locations">
                        <LocationMultiSelectField
                          options={fieldOptions.locations || []}
                          value={normalizeLocationFilterValues(prefDraft.locationsList || [])}
                          onChange={(nextValue) =>
                            setPrefDraft((prev) => ({
                              ...prev,
                              locationsList: normalizeLocationFilterValues(nextValue),
                              locationsCsv: normalizeLocationFilterValues(nextValue).join(", "),
                            }))
                          }
                          placeholder="Search or add preferred location"
                          inputSx={fieldSx}
                          autocompleteProps={{ groupBy: groupLocationOption }}
                        />
                      </PrefRow>
                    </Box>
                  );
                })()}
              </EditCard>
            ) : (
              renderPreferencesView()
            )}
          </Box>

          <Box>
            <SectionTitle
              title="Documents"
              onEdit={() => {
                if (activeEditSection === "documents") {
                  saveDocumentsSection();
                } else {
                  setActiveEditSection("documents");
                  resetOpsState(setDocsOps);
                }
              }}
              rightText={activeEditSection === "documents" ? "Save" : "Edit"}
            />

            <SectionOpsNotice
              state={docsOps}
              loadingText="Updating Documents…"
              onClearSuccess={() => setDocsOps((prev) => ({ ...prev, success: "" }))}
            />

            {activeEditSection === "documents" ? (
              <EditCard>
                <SubSectionHeader title="Documents" />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {resumeDocuments.length ? (
                    resumeDocuments.map((doc) => {
                      const fn = getDocFilename(doc);
                      return (
                        <DocRow
                          key={doc.id || fn}
                          doc={doc}
                          onRemove={removeDoc}
                          onOpen={openDoc}
                          removing={deleteState.loading && fn && deleteState.filename === fn}
                          canRemove={canRemoveDocument(doc)}
                        />
                      );
                    })
                  ) : (
                    <EmptyBox height={120} />
                  )}
                </Box>

                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlainPlusIcon />}
                    onClick={openFilePicker}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 400,
                      color: PURPLE,
                      borderColor: PURPLE,
                      height: 40,
                      minHeight: 40,
                      px: 2.2,
                      "&:hover": { borderColor: PURPLE },
                      "& .MuiButton-startIcon": { mr: 1 },
                    }}
                  >
                    Add More
                  </Button>
                </Box>
              </EditCard>
            ) : (
              renderDocumentsView()
            )}
          </Box>
        </>
      )}

      <Dialog open={companyScanDialogOpen} onClose={companyScanState.loading ? undefined : () => setCompanyScanDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle data-popup-header>Select experience companies to scan</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2, color: "#475467", fontSize: 14 }}>Choose the companies from this candidate’s work history whose opening jobs you want to find.</Typography>
          {companyExistenceLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CircularProgress size={18} />
              <Typography sx={{ color: LABEL, fontSize: 14 }}>Checking the Companies table…</Typography>
            </Box>
          ) : experienceCompanies.length ? experienceCompanies.map((name) => {
            const selected = selectedExperienceCompanies.includes(name);
            const alreadyExists = existingExperienceCompanyNames.has(name.toLowerCase());
            const toggle = () => setSelectedExperienceCompanies((current) => selected ? current.filter((item) => item !== name) : [...current, name]);
            return (
              <Box key={name} role="checkbox" aria-checked={selected} tabIndex={0} onClick={toggle} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); toggle(); } }} sx={{ mb: 1, px: 1.5, py: 1.2, border: `1px solid ${selected ? PURPLE : BORDER}`, borderRadius: 1.5, cursor: "pointer", display: "flex", alignItems: "center", gap: 1.25, "&:hover": { borderColor: PURPLE, bgcolor: "#FAF7FF" } }}>
                <Box onClick={(event) => event.stopPropagation()}>
                  <SquareToggle checked={selected} onClick={toggle} />
                </Box>
                <Typography sx={{ flex: 1, fontWeight: 600 }}>{name}</Typography>
                <Typography sx={{ fontSize: 12, color: alreadyExists ? "#067647" : LABEL, fontWeight: 600 }}>
                  {alreadyExists ? "Already exists" : "New company"}
                </Typography>
              </Box>
            );
          }) : <Alert severity="info">No companies were found in the candidate’s experience.</Alert>}
          {companyScanState.error ? <Alert severity="error" sx={{ mt: 2 }}>{companyScanState.error}</Alert> : null}
        </DialogContent>
        <DialogActions data-popup-footer>
          <Button onClick={() => setCompanyScanDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!selectedExperienceCompanies.length || companyScanState.loading || companyExistenceLoading} onClick={startExperienceCompanyScan}>Scan companies / opening jobs</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openingJobsDialogOpen} onClose={companyScanState.loading || companyScanState.saving ? undefined : () => setOpeningJobsDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle data-popup-header>Select opening jobs to add</DialogTitle>
        <DialogContent dividers>
          {companyScanState.loading ? <Box sx={{ py: 3 }}><Typography sx={{ mb: 1 }}>Finding openings for {companyScanState.companyName || "selected companies"} ({companyScanState.completed} of {companyScanState.total})</Typography><LinearProgress variant="determinate" value={companyScanState.total ? companyScanState.completed / companyScanState.total * 100 : 0} /></Box> : scannedOpeningJobs.length ? scannedOpeningJobs.map((job) => {
            const selected = selectedOpeningJobIds.includes(job.id);
            return <Box key={job.id} onClick={() => setSelectedOpeningJobIds((current) => selected ? current.filter((id) => id !== job.id) : [...current, job.id])} sx={{ cursor: "pointer", borderBottom: `1px solid ${BORDER}`, py: 1.25, display: "flex", gap: 1.5, alignItems: "center" }}><Box onClick={(event) => event.stopPropagation()}><SquareToggle checked={selected} onClick={() => setSelectedOpeningJobIds((current) => selected ? current.filter((id) => id !== job.id) : [...current, job.id])} /></Box><Box sx={{ flex: 1 }}><Typography sx={{ fontWeight: 700 }}>{job.job_title || job.title}</Typography><Typography sx={{ fontSize: 13, color: LABEL }}>{job.companyName}{job.location ? ` · ${job.location}` : ""}</Typography></Box><Typography sx={{ fontSize: 12, color: job.importTarget === "jobs" ? BLUE : "#067647", fontWeight: 700 }}>{job.importTarget === "jobs" ? "Job" : "Lead"}</Typography></Box>;
          }) : <Typography sx={{ py: 3, color: LABEL }}>No opening jobs were found. You can close this dialog and continue.</Typography>}
          {companyScanState.error ? <Alert severity="error" sx={{ mt: 2 }}>{companyScanState.error}</Alert> : null}
        </DialogContent>
        <DialogActions data-popup-footer>
          <Button disabled={companyScanState.saving} onClick={() => setOpeningJobsDialogOpen(false)}>Skip jobs</Button>
          <Button variant="contained" disabled={!selectedOpeningJobIds.length || companyScanState.saving} onClick={importSelectedOpeningJobs}>{companyScanState.saving ? "Adding jobs…" : `Add selected jobs (${selectedOpeningJobIds.length})`}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
